import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Papa from 'papaparse';
import { useMsal } from '@azure/msal-react';
import { acquireInstructionsToken, readInstruction } from '../services/instructionStorage';
import { PDFDocument, PDFPage, PDFFont, StandardFonts, rgb } from 'pdf-lib';
import { 
  getBlobUrlByConnectionString,
  getBlobVariablesCsvUrl, 
  getBlobVariablesCsvWriteUrl,
  getBlobServiceConfigUrl, 
  getBlobServiceConfigWriteUrl, 
  getSecondaryBlobServiceConfigWriteUrl,
  getSecondaryBlobVariablesCsvWriteUrl,
  getBlobPdfWriteUrl,
  getBlobPdfDeleteUrl
} from '../auth/blobConfig';
import { uploadFileToAzureOpenAI, createVectorStore, addFileToVectorStore, removeFileFromVectorStore, deleteAzureOpenAIFile } from '../services/azureOpenAI';
import { InstruccionServicio, parseInstruccionesServicio } from '../services/instruccionesServicio';
import SMLoadingSpinner from '../components/SMLoadingSpinner';
import AtencionPracticasPanel from '../components/AtencionPracticasPanel';
import { WhatsappChatPanel } from '../../../components/WhatsappChat';
import { ChevronLeft, Save, AlertCircle, ExternalLink, RefreshCw, FilePenLine, FileText, Maximize2, CalendarDays, UploadCloud, Trash2, Files, Plus, ThumbsUp, ThumbsDown, Search, Send } from 'lucide-react';
import { User } from '../../../../models/auth';
import { useCSVData, CSVRow, normalizeFuncionalidadValue } from '../hooks/useCSVData';
import plantillaBecasPdf from '../../../../assets/docs/plantilla_becas.pdf';

interface PromptVar {
  prompt: string;
  code: string;
  descripcion: string;
  selected?: boolean;
  source?: 'csv' | 'custom';
}

interface CustomPromptVarDefinition {
  Nombre_variable_en_prompt?: string;
  Nombre_variable_en_codigo?: string;
  Decripcion?: string;
  prompt?: string;
  code?: string;
  descripcion?: string;
}

interface PromptVarCsvDefinition {
  Nombre_variable_en_prompt: string;
  Nombre_variable_en_codigo: string;
  Decripcion: string;
}

interface ConversationMessage {
  role: string;
  content: string;
  date?: string;
  entorno?: string;
  contesta?: string;
  asistente?: string;
  comunicacion?: string;
  reason?: string | number;
  confidence?: string | number;
  llamada_funcion?: unknown; // función del sistema a la que ha llamado el asistente
  valoracion?: 'buena' | 'mala' | string; // valoración del usuario a esta respuesta (pulgares web / plantilla WhatsApp)
  puntuacion?: number;
  fecha_valoracion?: string;
  origen_valoracion?: 'conversacion' | 'plantilla_24h' | string;
}

interface ProcessedMessage {
  role: string;
  contentHtml: string;
  env: string;
  timestampMs: number;
  timestampLabel: string;
  dateKey: string;
  reason?: string | number;
  confidence?: string | number;
  llamadaFuncion?: string;
  valoracion?: string;
  fechaValoracion?: string;
  valoracionPorEncuesta24h?: boolean;
  canal: ConversationChannel;
}

interface ProcessedConversation {
  id: string;
  userId: string;
  userDisplay: string;
  dateKey: string;
  maxTimestampMs: number;
  messages: ProcessedMessage[];
  canales: ConversationChannel[];
}

type ConversationChannel = 'web' | 'app' | 'whatsapp' | 'otro';

// Encuesta de WhatsApp de 24h enviada a un usuario (clave "_encuestas_24h" de la respuesta de conversaciones)
interface SurveySentInfo {
  fecha?: string;
  servicio?: string;
  timestamp: number; // segundos
}
const SURVEYS_SENT_KEY = '_encuestas_24h';
const SURVEY_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const CHANNEL_LABELS: Record<ConversationChannel, string> = {
  web: 'Web',
  app: 'App',
  whatsapp: 'WhatsApp',
  otro: 'Otra vía',
};

// Vía de un mensaje según el campo "comunicacion" del log; los logs antiguos no lo tienen
const normalizeChannel = (comunicacion: string | undefined, userIdIsPhone: boolean): ConversationChannel => {
  const value = (comunicacion || '').trim().toLowerCase();
  if (value.includes('whatsapp')) return 'whatsapp';
  if (value === 'web') return 'web';
  if (value === 'app') return 'app';
  if (!value) return userIdIsPhone ? 'whatsapp' : 'otro';
  return 'otro';
};

interface ServiceDocument {
  nombre: string;
  rutaBlob: string;
  azureFileId?: string;
}

type UtdProcessRow = Record<string, string>;
type ServiceTableSource = 'utd' | 'becasColab';

interface ServiceDetailPageProps {
  user: User;
}

const DEFAULT_LANG = 'es-ES';
const CONVERSATION_TIME_ZONE = 'Europe/Madrid';
const BASE_DAYS = 28;
const PDF_UPLOAD_BATCH_SIZE = 10;
const VISIBLE_DOCUMENTS_LIMIT = 5;
const UTD_PROCESS_FILE_NAME = 'lista_procesos_UTD.csv';
const BECAS_COLAB_FILE_NAME = 'BecasColaboracion26-27/becas.json';
const BECAS_INTERESADO_PDF_BASE_URL = 'https://promptsprocesosceuswe.blob.core.windows.net/pdfsceu/Becas_colab_26-27';
const BECAS_CAMPUS_EMAIL_FLOW_URL = 'https://92a16a1d7d9ce257a088537e29eaab.51.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/74938769d8814994a53d56ae1df04e69/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=3SiU4yHVeGq_9klmbC9pyfV-0ewwDt0au9cklSX6_oQ';
const IMPAGOS_UCH_SERVICE_ID = 'impagosUCH';
const IMPAGOS_UCH_SEND_MESSAGE_ENDPOINT = 'https://gpt-ceu-python311-sp-prod-d4g0gth0h9aahaek.spaincentral-01.azurewebsites.net/api/enviar_mensaje_en_conversacion?code=3CHcVaqT942R87wos6jUkh9BP5BgW_XAxIcJAC8FUEnLAzFuPrzdKA==';
const UTD_PROCESS_DEFAULT_HEADERS = [
  'ID',
  'Nombre proceso',
  'Descripcion',
  'Enlace a formulario',
  'Enlace Power Apps',
  'Enlace Tutorial',
  'Centro',
  'usuarios_con_acceso',
  'Rol',
  'Preguntas tags',
];
const BECAS_COLAB_DEFAULT_HEADERS = [
  'Title',
  'Responsable',
  'correo',
  'Campus',
  'Departamento',
  'Plazas',
  'descripcion',
  'requisitos',
  'Horas_Semanales',
  'Importancia',
  'id_beca',
  'Nombre_Interesado',
  'Correo_interesado',
  'Notas_Medias',
  'nombre_plan_de_estudios',
  'curso_mas_alto_matriculado',
  'Turno_de_clase',
  'ID_lista',
];
const BECAS_USER_FIELDS = [
  { header: 'Importancia', label: 'Importancia' },
  { header: 'Nombre_Interesado', label: 'Nombre' },
  { header: 'Correo_interesado', label: 'Correo' },
  { header: 'Notas_Medias', label: 'Nota media' },
  { header: 'nombre_plan_de_estudios', label: 'Plan de estudios' },
  { header: 'curso_mas_alto_matriculado', label: 'Curso mas alto matriculado' },
  { header: 'Turno_de_clase', label: 'Turno de clase' },
];

function normalizeText(value: string | undefined): string {
  return (value || '').trim().toLowerCase();
}

function normalizeComparableText(value: string | undefined): string {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function mergeSelectedFiles(currentFiles: File[], newFiles: File[]): File[] {
  const filesByIdentity = new Map<string, File>();

  [...currentFiles, ...newFiles].forEach((file) => {
    const identity = `${file.name}-${file.size}-${file.lastModified}`;
    filesByIdentity.set(identity, file);
  });

  return Array.from(filesByIdentity.values());
}

function createEmptyUtdProcessRow(headers: string[]): UtdProcessRow {
  return headers.reduce<UtdProcessRow>((acc, header) => {
    acc[header] = '';
    return acc;
  }, {});
}

function getDefaultServiceTableHeaders(source: ServiceTableSource): string[] {
  return source === 'becasColab'
    ? [...BECAS_COLAB_DEFAULT_HEADERS]
    : [...UTD_PROCESS_DEFAULT_HEADERS];
}

function serializeUtdProcessSnapshot(headers: string[], rows: UtdProcessRow[]): string {
  return JSON.stringify({ headers, rows });
}

function csvEscapeSemicolon(value: string): string {
  const normalized = String(value ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (/[;"\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function buildUtdProcessCsv(headers: string[], rows: UtdProcessRow[]): string {
  const headerLine = headers.map(csvEscapeSemicolon).join(';');
  const rowLines = rows.map((row) =>
    headers.map((header) => csvEscapeSemicolon(row[header] || '')).join(';')
  );
  return [headerLine, ...rowLines].join('\n');
}

function stringifyBecasCell(header: string, value: unknown): string {
  if (Array.isArray(value)) {
    const values = value
      .map((item) => {
        if (item == null) return '';
        return typeof item === 'object' ? JSON.stringify(item) : String(item);
      });
    return (isBecasUserField(header) ? values : values.filter(Boolean)).join('\n');
  }
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function isBecasArrayField(header: string): boolean {
  const normalizedHeader = normalizeComparableText(header);
  return [
    'responsable',
    'correo',
    'importancia',
    'nombre_interesado',
    'correo_interesado',
    'notas_medias',
    'nombre_plan_de_estudios',
    'curso_mas_alto_matriculado',
    'turno_de_clase',
  ].includes(normalizedHeader);
}

function isBecasUserField(header: string): boolean {
  const normalizedHeader = normalizeComparableText(header);
  return BECAS_USER_FIELDS.some((field) => normalizeComparableText(field.header) === normalizedHeader);
}

function splitBecasUserValues(value: string): string[] {
  if (!value) return [];
  return String(value).split(/\r?\n/);
}

function serializeBecasUserValues(values: string[]): string {
  const trimmedValues = [...values];
  while (trimmedValues.length && !String(trimmedValues[trimmedValues.length - 1] || '').trim()) {
    trimmedValues.pop();
  }
  return trimmedValues.join('\n');
}

function getBecasUserValue(row: UtdProcessRow, header: string, userIndex: number): string {
  return splitBecasUserValues(row[header] || '')[userIndex] || '';
}

function buildBecasInteresadoPdfUrl(email: string): string {
  const safeEmail = String(email || '').trim();
  return safeEmail ? `${BECAS_INTERESADO_PDF_BASE_URL}/${encodeURIComponent(safeEmail)}.pdf` : '';
}

function parseBecasCell(header: string, value: string): unknown {
  const trimmedValue = String(value ?? '').trim();
  const normalizedHeader = normalizeComparableText(header);

  if (isBecasArrayField(header)) {
    if (!trimmedValue) return [];
    const values = String(value ?? '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map((item) => item.trim());
    return isBecasUserField(header) ? values : values.filter(Boolean);
  }

  if (['plazas', 'horas_semanales', 'id_lista'].includes(normalizedHeader)) {
    if (!trimmedValue) return null;
    const numericValue = Number(trimmedValue);
    return Number.isNaN(numericValue) ? trimmedValue : numericValue;
  }

  return value ?? '';
}

function isBecasIdField(header: string): boolean {
  const normalizedHeader = normalizeComparableText(header);
  return normalizedHeader === 'id_lista';
}

function formatServiceTableId(source: ServiceTableSource | null, header: string, value: string): string {
  if (source === 'becasColab' && normalizeComparableText(header) === 'id_beca') {
    return String(value || '').replace(/^#+\s*/, '');
  }
  return value;
}

function buildBecasJson(headers: string[], rows: UtdProcessRow[]): string {
  const jsonRows = rows.map((row) =>
    headers.reduce<Record<string, unknown>>((acc, header) => {
      acc[header] = parseBecasCell(header, row[header] || '');
      return acc;
    }, {})
  );
  return JSON.stringify(jsonRows, null, 2);
}

function getBecasValue(row: UtdProcessRow, keys: string[]): string {
  const normalizedKeys = keys.map((key) => normalizeComparableText(key));
  const match = Object.entries(row).find(([key]) =>
    normalizedKeys.includes(normalizeComparableText(key))
  );
  return match ? String(match[1] || '').trim() : '';
}

function getBecasCampus(row: UtdProcessRow): string {
  return getBecasValue(row, ['Campus']) || 'Sin campus';
}

function sanitizeBlobFileName(value: string): string {
  const normalized = normalizeComparableText(value)
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || 'sin_campus';
}

function groupBecasByCampus(rows: UtdProcessRow[]): Array<[string, UtdProcessRow[]]> {
  const grouped = rows.reduce<Map<string, UtdProcessRow[]>>((acc, row) => {
    const campus = getBecasCampus(row);
    const items = acc.get(campus) || [];
    items.push(row);
    acc.set(campus, items);
    return acc;
  }, new Map());

  return Array.from(grouped.entries()).sort(([a], [b]) =>
    a.localeCompare(b, DEFAULT_LANG, { sensitivity: 'base' })
  );
}

function wrapPdfText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const paragraphs = String(text || '-').split(/\r?\n/);
  const lines: string[] = [];

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      return;
    }

    let currentLine = '';
    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        currentLine = candidate;
        return;
      }
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    });
    if (currentLine) lines.push(currentLine);
  });

  return lines;
}

function extractBecasPart(rawValue: string, label: string): string {
  const regex = new RegExp(`(?:^|\\|)\\s*${label}\\s*:\\s*([^|]+)`, 'i');
  const match = String(rawValue || '').match(regex);
  return match ? match[1].trim() : '';
}

function formatBecasAmount(directValue: string, rawValue: string): string {
  const amount = directValue || extractBecasPart(rawValue, 'Importe mes');
  if (!amount) return '-';
  return /€|eur/i.test(amount) ? amount : `${amount} €/mes`;
}

function drawBecasCardCell(
  page: PDFPage,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fonts: { regular: PDFFont; bold: PDFFont },
  labelWidth = 78
) {
  page.drawRectangle({
    x,
    y,
    width,
    height,
    borderWidth: 0.35,
    borderColor: rgb(0.78, 0.82, 0.88),
    color: rgb(0.98, 0.99, 1),
  });
  page.drawText(label, {
    x: x + 8,
    y: y + height / 2 - 3,
    size: 7.5,
    font: fonts.bold,
    color: rgb(0.19, 0.25, 0.35),
  });

  const valueX = x + labelWidth;
  const valueWidth = width - labelWidth - 8;
  const valueLines = wrapPdfText(value || '-', fonts.regular, 7.5, valueWidth).slice(0, 2);
  const startY = y + height / 2 - 3 + (valueLines.length > 1 ? 4.5 : 0);
  valueLines.forEach((line, lineIndex) => {
    page.drawText(line, {
      x: valueX,
      y: startY - lineIndex * 8.5,
      size: 7.5,
      font: fonts.regular,
      color: rgb(0.12, 0.18, 0.28),
    });
  });
}

async function createBecasCampusPdfBytes(
  campus: string,
  rows: UtdProcessRow[],
  templateBytes: ArrayBuffer
): Promise<Uint8Array> {
  const templateDoc = await PDFDocument.load(templateBytes);
  const pdfDoc = await PDFDocument.create();
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const templatePageIndex = 0;
  const templatePage = templateDoc.getPage(templatePageIndex);
  const templateSize = templatePage.getSize();
  const marginX = 44;
  const headerCampusY = 670;
  const firstPageContentTop = 612;
  const nextPageTopY = templateSize.height - 56;
  const bottomY = 52;
  let page: PDFPage | null = null;
  let y = firstPageContentTop;

  const addPdfPage = async () => {
    const isFirstPage = pdfDoc.getPageCount() === 0;

    if (isFirstPage) {
      const [copiedPage] = await pdfDoc.copyPages(templateDoc, [templatePageIndex]);
      pdfDoc.addPage(copiedPage);
      page = copiedPage;
      copiedPage.drawText(campus, {
        x: 104,
        y: headerCampusY,
        size: 16,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
      y = firstPageContentTop;
      return;
    }

    page = pdfDoc.addPage([templateSize.width, templateSize.height]);
    y = nextPageTopY;
  };

  await addPdfPage();

  const cardWidth = templateSize.width - marginX * 2;
  const headerHeight = 24;
  const rowHeight = 22;
  const textBlockWidth = cardWidth - 20;
  const textLineHeight = 9;
  const sectionTitleGap = 12;
  const sectionSpacing = 8;
  const cardTopGap = 14;
  const cardBottomPad = 12;
  const darkBlue = rgb(0.06, 0.20, 0.36);

  for (const [index, row] of rows.entries()) {
    const title = getBecasValue(row, ['Title']) || `Beca ${index + 1}`;
    const id = formatServiceTableId('becasColab', 'id_beca', getBecasValue(row, ['id_beca']));
    const plazas = getBecasValue(row, ['Plazas']);
    const horas = getBecasValue(row, ['Horas_Semanales']);
    const descripcion = getBecasValue(row, ['descripcion']);
    const requisitos = getBecasValue(row, ['requisitos']);
    const departamento = getBecasValue(row, ['Departamento', 'departamento']) || '-';
    const modalidad = getBecasValue(row, ['Modalidad', 'modalidad']) || extractBecasPart(descripcion, 'Modalidad') || '-';
    const fechaInicio = getBecasValue(row, ['Fecha_Inicio', 'Fecha Inicio', 'fecha_inicio']);
    const fechaFin = getBecasValue(row, ['Fecha_Fin', 'Fecha Fin', 'fecha_fin']);
    const periodo = getBecasValue(row, ['Periodo', 'periodo']) || 'Todo el Curso (Octubre - 31 de julio)';
    const curso = extractBecasPart(requisitos, 'Curso') || 'Indiferente';
    const titulacion = extractBecasPart(requisitos, 'Titulación') || requisitos || '-';
    const otrosRequisitos = extractBecasPart(requisitos, 'Otros');
    const tareas = extractBecasPart(descripcion, 'Tareas') || descripcion || '-';
    const requisitosText = otrosRequisitos ? `${titulacion}. ${otrosRequisitos}` : titulacion;

    const requisitosLines = wrapPdfText(requisitosText, regularFont, 7.5, textBlockWidth).slice(0, 5);
    const tareasLines = wrapPdfText(tareas, regularFont, 7.5, textBlockWidth).slice(0, 6);

    const tableHeight = rowHeight * 4;
    const requisitosBlockHeight = sectionTitleGap + requisitosLines.length * textLineHeight;
    const tareasBlockHeight = sectionTitleGap + tareasLines.length * textLineHeight;
    const cardHeight =
      headerHeight +
      tableHeight +
      cardTopGap +
      requisitosBlockHeight +
      sectionSpacing +
      tareasBlockHeight +
      cardBottomPad;

    if (!page || y - cardHeight < bottomY) {
      await addPdfPage();
    }

    const cardX = marginX;
    const cardBottom = y - cardHeight;

    page!.drawRectangle({
      x: cardX,
      y: cardBottom,
      width: cardWidth,
      height: cardHeight,
      borderWidth: 0.6,
      borderColor: rgb(0.78, 0.82, 0.88),
      color: rgb(1, 1, 1),
    });
    page!.drawRectangle({
      x: cardX,
      y: y - headerHeight,
      width: cardWidth,
      height: headerHeight,
      color: darkBlue,
    });
    page!.drawText(`${id || index + 1}-${title}`, {
      x: cardX + 10,
      y: y - 15,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });

    const halfWidth = cardWidth / 2;
    const rightX = cardX + halfWidth;
    const tableTop = y - headerHeight;

    drawBecasCardCell(page!, 'Departamento', departamento, cardX, tableTop - rowHeight, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 78);
    drawBecasCardCell(page!, 'Periodo', periodo, rightX, tableTop - rowHeight, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 52);
    drawBecasCardCell(page!, 'Carga Horaria', horas ? `${horas} horas semanales` : '-', cardX, tableTop - rowHeight * 2, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 78);
    const importe = getBecasValue(row, ['Cuantia', 'Cuantía', 'Importe_mes', 'Importe mes', 'Importe']);
    drawBecasCardCell(page!, 'Plazas / Cuantía', `${plazas || '-'} plaza${String(plazas) === '1' ? '' : 's'} / ${formatBecasAmount(importe, descripcion)}`, rightX, tableTop - rowHeight * 2, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 92);
    drawBecasCardCell(page!, 'Modalidad', modalidad, cardX, tableTop - rowHeight * 3, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 78);
    drawBecasCardCell(page!, 'Curso Destinado', curso, rightX, tableTop - rowHeight * 3, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 92);
    drawBecasCardCell(page!, 'Fecha Inicio', fechaInicio || '-', cardX, tableTop - rowHeight * 4, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 78);
    drawBecasCardCell(page!, 'Fecha Fin', fechaFin || '-', rightX, tableTop - rowHeight * 4, halfWidth, rowHeight, { regular: regularFont, bold: boldFont }, 92);

    let textY = tableTop - tableHeight - cardTopGap;
    page!.drawText('REQUISITOS DE TITULACIÓN', {
      x: cardX + 10,
      y: textY,
      size: 8,
      font: boldFont,
      color: darkBlue,
    });
    textY -= sectionTitleGap;
    for (const line of requisitosLines) {
      page!.drawText(line, {
        x: cardX + 10,
        y: textY,
        size: 7.5,
        font: regularFont,
        color: rgb(0.12, 0.18, 0.28),
      });
      textY -= textLineHeight;
    }

    textY -= sectionSpacing;
    page!.drawText('TAREAS Y FUNCIONES', {
      x: cardX + 10,
      y: textY,
      size: 8,
      font: boldFont,
      color: darkBlue,
    });
    textY -= sectionTitleGap;
    for (const line of tareasLines) {
      page!.drawText(line, {
        x: cardX + 10,
        y: textY,
        size: 7.5,
        font: regularFont,
        color: rgb(0.12, 0.18, 0.28),
      });
      textY -= textLineHeight;
    }

    y = cardBottom - 16;
  }

  return pdfDoc.save();
}

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = '';
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...Array.from(chunk));
  }
  return btoa(binary);
}

function buildBecasCampusEmailHtml(campus: string, becasCount: number, userName: string): string {
  const safeCampus = escapeHtml(campus);
  const safeName = escapeHtml(userName || 'usuario');
  const countLabel = `${becasCount} beca${becasCount === 1 ? '' : 's'}`;

  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Becas de colaboración por campus</title><style>body { font-family: 'Poppins', Verdana, sans-serif; margin: 0; padding: 40px 0; background-color: #ffffff; } table { width: 100%; border-collapse: collapse; } td { text-align: center; } .logo { text-align: center; margin-bottom: 25px; } .logo img { width: 180px; max-width: 180px; height: auto; display: block; margin-left: auto; margin-right: auto; } .carta { max-width: 750px; margin: 0 auto; background: #ffffff; padding: 40px 30px; border: 1px solid #003CA3; border-radius: 12px; box-shadow: 0 4px 12px rgba(0, 60, 163, 0.2); text-align: center; } p { font-size: 15px; color: #000000; line-height: 1.8; margin: 0 0 20px 0; } a { color: #188FF1; text-decoration: none; } a:hover { text-decoration: underline; } hr { margin: 30px auto; border: none; border-top: 1px solid #003CA3; width: 65%; } .footer { font-size: 13px; color: #000000; margin-top: 30px; } .datos { text-align: left; margin: 0 auto 25px auto; max-width: 600px; color: #000000; font-size: 15px; line-height: 1.8; } .boton { display: inline-block; background-color: #003CA3; color: #ffffff !important; padding: 14px 28px; border-radius: 8px; font-size: 15px; font-weight: bold; text-decoration: none; margin: 20px 0; } .boton:hover { text-decoration: none; background-color: #188FF1; } </style></head><body><table><tr><td><div class="carta"><div class="logo"><img src="https://promptsprocesosceuswe.blob.core.windows.net/imagenesgraficas/Logo-positivo_RGB.png" alt="Logo CEU" width="180" style="width:180px;max-width:180px;height:auto;"></div><p>Hola ${safeName},</p><p>Se ha generado el documento de becas de colaboración correspondiente al campus <strong>${safeCampus}</strong>.</p><div class="datos"><strong>Campus:</strong> ${safeCampus}<br><strong>Número de registros:</strong> ${countLabel}<br><strong>Documento:</strong> PDF adjunto en este correo.</div><p>Adjunto encontrarás el documento actualizado para su revisión o distribución.</p><hr><p class="footer">Este es un mensaje informativo generado por el sistema de gestión de servicios de CEU.</p></div></td></tr></table></body></html>`;
}

async function sendBecasCampusPdfsByEmail(rows: UtdProcessRow[], user: User): Promise<number> {
  const groupedByCampus = groupBecasByCampus(rows);
  if (!groupedByCampus.length) return 0;

  const templateResponse = await fetch(plantillaBecasPdf);
  if (!templateResponse.ok) {
    throw new Error(`No se pudo cargar la plantilla PDF de becas (${templateResponse.status}).`);
  }
  const templateBytes = await templateResponse.arrayBuffer();

  await Promise.all(groupedByCampus.map(async ([campus, campusRows]) => {
    const pdfBytes = await createBecasCampusPdfBytes(campus, campusRows, templateBytes);
    const fileName = `becas_${sanitizeBlobFileName(campus)}.pdf`;
    const response = await fetch(BECAS_CAMPUS_EMAIL_FLOW_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        asunto: `Becas de colaboración - ${campus}`,
        cuerpo: buildBecasCampusEmailHtml(campus, campusRows.length, user.name || user.email),
        nombre_archivo: fileName,
        contenidob64: bytesToBase64(pdfBytes),
        user_mail: user.email,
      }),
    });

    if (!response.ok && response.status !== 502) {
      throw new Error(`No se pudo enviar el correo del campus ${campus} (${response.status}).`);
    }
    if (response.status === 502) {
      console.warn(`Power Automate devolvió 502 para ${campus}, pero la petición pudo quedar encolada.`);
    }
  }));

  return groupedByCampus.length;
}

function isUtdMultilineField(header: string): boolean {
  const normalizedHeader = normalizeComparableText(header);
  return (
    normalizedHeader === 'descripcion' ||
    normalizedHeader === 'requisitos' ||
    normalizedHeader === 'usuarios_con_acceso' ||
    normalizedHeader === 'preguntas tags' ||
    isBecasArrayField(header)
  );
}

function maskPhone(value: string): string {
  const digits = String(value || '').replace(/[^\d+]/g, '');
  if (!/^\+?\d{5,}$/.test(digits)) return value;
  return `${'*'.repeat(Math.max(1, digits.length - 5))}${digits.slice(-5)}`;
}

function parseDateParts(value: string | undefined): { timestampMs: number; dateKey: string; full: string } {
  if (!value) return { timestampMs: 0, dateKey: 'Fecha desconocida', full: '' };

  const raw = String(value);
  const m = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})/);
  let d: Date | null = null;
  if (m) {
    const [, day, month, year, hh, mm, ss] = m;
    d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hh), Number(mm), Number(ss)));
  } else {
    let normalized = raw.replace(/\//g, '-');
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(normalized) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(normalized)) {
      normalized = `${normalized.replace(' ', 'T')}Z`;
    }
    const ts = Date.parse(normalized);
    if (!Number.isNaN(ts)) d = new Date(ts);
  }

  if (!d || Number.isNaN(d.getTime())) return { timestampMs: 0, dateKey: 'Fecha desconocida', full: raw };

  const dateKey = d.toLocaleDateString(DEFAULT_LANG, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: CONVERSATION_TIME_ZONE,
  });
  const full = d.toLocaleString(DEFAULT_LANG, { timeZone: CONVERSATION_TIME_ZONE });
  return { timestampMs: d.getTime(), dateKey, full };
}

function sanitizeHTML(html: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = html || '';
  tpl.content.querySelectorAll('script').forEach((n) => n.remove());
  tpl.content.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const n = attr.name.toLowerCase();
      const v = String(attr.value || '');
      if (n.startsWith('on')) el.removeAttribute(attr.name);
      if ((n === 'href' || n === 'src') && /^javascript:/i.test(v)) el.removeAttribute(attr.name);
    });
  });
  return tpl.innerHTML;
}

function isImplementedService(value: string | undefined): boolean {
  return normalizeComparableText(value).includes('implementado');
}

function getFieldValue(row: CSVRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function getServiceName(row: CSVRow): string {
  return getFieldValue(row, ['NombreProceso', 'Nombre del servicio']);
}

function getServiceId(row: CSVRow): string {
  const preferredKeys = [
    'ID',
    'ID servicio',
    'Id servicio',
    'id servicio',
    'ID del servicio',
    'Id del servicio',
    'id del servicio',
    'service_id',
    'servicio_id',
  ];

  for (const key of preferredKeys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  const dynamicKey = Object.keys(row).find((key) => {
    const normalized = key.toLowerCase();
    return normalized === 'id' || (normalized.includes('id') && normalized.includes('servicio'));
  });

  if (dynamicKey) {
    const value = row[dynamicKey];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function getServiceStatus(row: CSVRow): string {
  return getFieldValue(row, ['Estado_implementacion', 'Estado implementación']);
}

function ensurePromptFileName(value: string): string {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return /\.(txt|md)$/i.test(normalized) ? normalized : `${normalized}.txt`;
}

function buildPromptBlobPath(folderName: string, promptFileName: string): string {
  const normalizedPrompt = ensurePromptFileName(promptFileName).replace(/^\/+/, '');
  if (normalizedPrompt.includes('/')) return normalizedPrompt;
  return `${folderName}/${normalizedPrompt}`;
}

function buildDeployedPromptBlobPath(folderName: string, promptFileName: string): string {
  const normalizedPrompt = ensurePromptFileName(promptFileName).replace(/^\/+/, '');
  const fileName = normalizedPrompt.split('/').filter(Boolean).pop() || normalizedPrompt;
  return `${folderName}/${fileName}`;
}

function getResponseLastModifiedMs(response: Response): number | null {
  const lastModified = response.headers.get('Last-Modified');
  if (!lastModified) return null;
  const timestamp = Date.parse(lastModified);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function parseCustomPromptVars(raw: unknown): PromptVar[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const def = item as CustomPromptVarDefinition;
      const prompt = String(def.Nombre_variable_en_prompt ?? def.prompt ?? '').trim();
      const code = String(def.Nombre_variable_en_codigo ?? def.code ?? '').trim();
      const descripcion = String(def.Decripcion ?? def.descripcion ?? '').trim();
      if (!prompt || !code) return null;
      return {
        prompt,
        code,
        descripcion: descripcion || 'Variable personalizada',
        source: 'custom' as const,
      };
    })
    .filter(Boolean) as PromptVar[];
}

function parseVariablesCsvDefinitions(csvText: string): PromptVarCsvDefinition[] {
  const parsed = Papa.parse<Record<string, unknown>>(csvText, {
    header: true,
    delimiter: ';',
    skipEmptyLines: true,
  });

  if (parsed.errors.length > 0) {
    throw new Error(parsed.errors[0].message || 'Error al parsear lista_variables_servicios.csv.');
  }

  const readCsvCell = (row: Record<string, unknown>, candidateKeys: string[]): string => {
    const normalizedCandidates = candidateKeys.map((k) => normalizeComparableText(k));
    for (const [rawKey, rawValue] of Object.entries(row || {})) {
      const key = normalizeComparableText(String(rawKey || '').replace(/^\uFEFF/, ''));
      if (!normalizedCandidates.includes(key)) continue;
      return String(rawValue ?? '').trim();
    }
    return '';
  };

  return (parsed.data || [])
    .map((row) => {
      const safeRow = (row || {}) as Record<string, unknown>;
      return {
        Nombre_variable_en_prompt: readCsvCell(safeRow, ['Nombre_variable_en_prompt']),
        Nombre_variable_en_codigo: readCsvCell(safeRow, ['Nombre_variable_en_codigo']),
        Decripcion: readCsvCell(safeRow, ['Decripcion', 'Descripcion']),
      };
    })
    .filter((row) => row.Nombre_variable_en_prompt && row.Nombre_variable_en_codigo);
}

function buildVariablesCsv(definitions: PromptVarCsvDefinition[]): string {
  const headers = ['Nombre_variable_en_prompt', 'Nombre_variable_en_codigo', 'Decripcion'];
  const rows = definitions.map((row) =>
    [
      csvEscapeSemicolon(row.Nombre_variable_en_prompt),
      csvEscapeSemicolon(row.Nombre_variable_en_codigo),
      csvEscapeSemicolon(row.Decripcion),
    ].join(';')
  );
  return [headers.join(';'), ...rows].join('\n');
}

function formatBlobModifiedDate(timestampMs: number | null): string {
  if (timestampMs === null) return '-';
  return new Date(timestampMs).toLocaleString(DEFAULT_LANG);
}

function extractEmails(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      return arr
        .map((item: { user_mail?: string }) => item.user_mail?.toLowerCase().trim())
        .filter(Boolean) as string[];
    }
  } catch {
    // Campo no es JSON valido
  }
  return [];
}

function isMessagesArray(value: unknown): value is ConversationMessage[] {
  return Array.isArray(value);
}

function isPhoneUserId(userId: string): boolean {
  return /^\+?\d+$/.test(userId.trim());
}

function isEmailUserId(userId: string): boolean {
  return userId.includes('@');
}

function processConversationByDay(
  messages: ConversationMessage[],
  processName: string,
  userId: string,
  idSuffix: string,
  filterByProcess = true
): ProcessedConversation[] {
  const normalizedProcessName = normalizeComparableText(processName);
  const isProcessRole = (role: string | undefined) =>
    normalizeComparableText(role) === normalizedProcessName;
  const filteredIdx: number[] = filterByProcess
    ? []
    : messages.map((_, index) => index);

  if (filterByProcess) {
    for (let i = 0; i < messages.length; i += 1) {
      const role = messages[i]?.role || '';
      if (isProcessRole(role)) {
        filteredIdx.push(i);
        continue;
      }
      if (role === 'user') {
        const prevIs = i > 0 && isProcessRole(messages[i - 1]?.role);
        const nextIs = i < messages.length - 1 && isProcessRole(messages[i + 1]?.role);
        if (prevIs || nextIs) filteredIdx.push(i);
      }
    }
  }

  if (!filteredIdx.length) return [];

  const looksPhone = isPhoneUserId(userId);
  const formatLlamadaFuncion = (value: unknown): string | undefined => {
    if (value == null || value === '') return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };
  const filtered: ProcessedMessage[] = filteredIdx.map((idx) => {
    const msg = messages[idx];
    const { timestampMs, dateKey, full } = parseDateParts(msg?.date);
    return {
      role: msg?.role || '',
      contentHtml: sanitizeHTML(msg?.content || ''),
      env: (msg?.entorno || 'Producción').trim(),
      timestampMs,
      timestampLabel: full || msg?.date || '',
      dateKey,
      reason: msg?.reason,
      confidence: msg?.confidence,
      llamadaFuncion: formatLlamadaFuncion(msg?.llamada_funcion),
      valoracion: msg?.valoracion,
      fechaValoracion: msg?.fecha_valoracion,
      valoracionPorEncuesta24h: msg?.origen_valoracion === 'plantilla_24h',
      canal: normalizeChannel(msg?.comunicacion, looksPhone),
    };
  });

  const userDisplay = looksPhone ? `Teléfono: ${maskPhone(userId)}` : userId;
  const groupedByDay: Record<string, ProcessedMessage[]> = {};

  for (const message of filtered) {
    groupedByDay[message.dateKey] ??= [];
    groupedByDay[message.dateKey].push(message);
  }

  return Object.entries(groupedByDay)
    .map(([dateKey, dayMessages]) => {
      const sortedMessages = [...dayMessages].sort((a, b) => a.timestampMs - b.timestampMs);
      const maxTimestampMs = sortedMessages.reduce((acc, m) => (m.timestampMs > acc ? m.timestampMs : acc), 0);

      return {
        id: `${dateKey}::${userId}::${idSuffix}`,
        userId,
        userDisplay,
        dateKey,
        maxTimestampMs,
        messages: sortedMessages,
        canales: Array.from(new Set(sortedMessages.map((m) => m.canal))),
      };
    })
    .sort((a, b) => b.maxTimestampMs - a.maxTimestampMs);
}

const ServiceDetailPage: React.FC<ServiceDetailPageProps> = ({ user }) => {
  const { instance, accounts } = useMsal();
  const instructionsToken = useCallback(() => acquireInstructionsToken(instance, accounts), [instance, accounts]);
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const serviceName = name ? decodeURIComponent(name) : '';
  const serviceId = new URLSearchParams(location.search).get('sid')?.trim() || '';
  const serviceIdentifier = serviceId || serviceName;
  const userEmail = user.email.toLowerCase().trim();
  const { data: servicesData, loading: loadingAccess, error: accessError } = useCSVData();

  const [vars, setVars] = useState<PromptVar[]>([]);
  const [loadingVars, setLoadingVars] = useState(true);
  const [varError, setVarError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCreateVarForm, setShowCreateVarForm] = useState(false);
  const [newVarPrompt, setNewVarPrompt] = useState('');
  const [newVarDescription, setNewVarDescription] = useState('');
  const [newVarCode, setNewVarCode] = useState('');
  const [newVarError, setNewVarError] = useState<string | null>(null);
  const [editingVarPrompt, setEditingVarPrompt] = useState<string | null>(null);
  const [editVarPrompt, setEditVarPrompt] = useState('');
  const [editVarDescription, setEditVarDescription] = useState('');
  const [editVarCode, setEditVarCode] = useState('');
  const [editVarError, setEditVarError] = useState<string | null>(null);
  const [editingVarSaving, setEditingVarSaving] = useState(false);
  const [deletingVarPrompt, setDeletingVarPrompt] = useState<string | null>(null);
  const [messageOverlay, setMessageOverlay] = useState<{ show: boolean; text: string }>({ show: false, text: '' });


  const [editablePromptText, setEditablePromptText] = useState('');
  const [promptFileName, setPromptFileName] = useState('');
  const [instructionFiles, setInstructionFiles] = useState<InstruccionServicio[]>([]);
  const [loadingText, setLoadingText] = useState(true);
  const [textError, setTextError] = useState<string | null>(null);
  const [publishingPrompt, setPublishingPrompt] = useState(false);
  const [promptPublishError, setPromptPublishError] = useState<string | null>(null);
  const [promptPublishSuccess, setPromptPublishSuccess] = useState<string | null>(null);
  const [promptLastModifiedMs, setPromptLastModifiedMs] = useState<number | null>(null);
  const [deployedPromptLastModifiedMs, setDeployedPromptLastModifiedMs] = useState<number | null>(null);
  const [checkingDeployedPrompt, setCheckingDeployedPrompt] = useState(false);
  const [deployedPromptError, setDeployedPromptError] = useState<string | null>(null);
  const [conversationsRaw, setConversationsRaw] = useState<unknown | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [conversationBackend, setConversationBackend] = useState<'ES' | 'SWE'>('ES');
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState<'todas' | 'valoradas' | 'buena' | 'mala'>('todas');
  const [channelFilter, setChannelFilter] = useState<'todas' | ConversationChannel>('todas');
  // Estado de la encuesta de 24h por usuario (conv.userId): un usuario puede tener varias tarjetas (una por día)
  const [surveySending, setSurveySending] = useState<Record<string, boolean>>({});
  const [surveyFeedback, setSurveyFeedback] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});
  const [surveySentLocal, setSurveySentLocal] = useState<Record<string, SurveySentInfo>>({});
  const surveyInFlightRef = useRef<Set<string>>(new Set());
  const [resetMfaJiraId, setResetMfaJiraId] = useState('');
  const [resetMfaSearching, setResetMfaSearching] = useState(false);
  const [resetMfaFeedback, setResetMfaFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [resetMfaSending, setResetMfaSending] = useState(false);
  const [resetMfaIncidencia, setResetMfaIncidencia] = useState<{
    id_incidencia: string;
    estado?: string;
    resumen?: string;
    descripcion?: string;
    mail_ceu?: string;
    nombre?: string;
  } | null>(null);
  const [hidePhoneConversations, setHidePhoneConversations] = useState(false);
  const [hideEmailConversations, setHideEmailConversations] = useState(false);
  const [expandedConversations, setExpandedConversations] = useState<Record<string, boolean>>({});
  const [copiedConversationId, setCopiedConversationId] = useState<string | null>(null);
  const [openMessageInfo, setOpenMessageInfo] = useState<string | null>(null);
  const [conversationRefreshToken, setConversationRefreshToken] = useState(0);
  const [impagosMessageDrafts, setImpagosMessageDrafts] = useState<Record<string, string>>({});
  const [impagosSendingMessages, setImpagosSendingMessages] = useState<Record<string, boolean>>({});
  const [impagosMessageFeedback, setImpagosMessageFeedback] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});
  const todayDate = new Date();
  const defaultStartDate = new Date(todayDate.getTime() - BASE_DAYS * 24 * 60 * 60 * 1000);
  const [startDateInput, setStartDateInput] = useState(defaultStartDate.toISOString().slice(0, 10));
  const [endDateInput, setEndDateInput] = useState(todayDate.toISOString().slice(0, 10));
  const [appliedDateRange, setAppliedDateRange] = useState<{ startMs: number; endMs: number } | null>(null);
  const [dateFilterError, setDateFilterError] = useState<string | null>(null);

  // PDF Upload State
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [serviceDocuments, setServiceDocuments] = useState<ServiceDocument[]>([]);
  const [deletingDocumentKey, setDeletingDocumentKey] = useState<string | null>(null);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [utdProcessHeaders, setUtdProcessHeaders] = useState<string[]>([]);
  const [utdProcessRows, setUtdProcessRows] = useState<UtdProcessRow[]>([]);
  const [loadingUtdProcesses, setLoadingUtdProcesses] = useState(false);
  const [utdProcessError, setUtdProcessError] = useState<string | null>(null);
  const [utdProcessSuccess, setUtdProcessSuccess] = useState<string | null>(null);
  const [savingUtdProcesses, setSavingUtdProcesses] = useState(false);
  const [utdProcessSnapshot, setUtdProcessSnapshot] = useState(() =>
    serializeUtdProcessSnapshot([], [])
  );
  const [utdEditorRowIndex, setUtdEditorRowIndex] = useState<number | null>(null);
  const [utdEditorIsNew, setUtdEditorIsNew] = useState(false);
  const [utdSearch, setUtdSearch] = useState('');
  const [utdVisibleCount, setUtdVisibleCount] = useState(10);
  const [collapsedBecasUsers, setCollapsedBecasUsers] = useState<Record<string, boolean>>({});

  const conversationsEsApiUrl = process.env.REACT_APP_SM_CONVERSATIONS_ES_API_URL as string;
  const conversationsEsApiKey = process.env.REACT_APP_SM_CONVERSATIONS_ES_API_KEY as string;
  const conversationsSweApiUrl = process.env.REACT_APP_SM_CONVERSATIONS_SWE_API_URL as string;
  const conversationsSweApiKey = process.env.REACT_APP_SM_CONVERSATIONS_SWE_API_KEY as string;
  // Endpoint enviar_notificacion_servicio (accion=encuesta_whatsapp) del mismo backend (ES/SWE) del que se leen las conversaciones
  const notificacionEsApiUrl = process.env.REACT_APP_SM_NOTIFICACION_ES_API_URL as string;
  const notificacionEsApiKey = process.env.REACT_APP_SM_NOTIFICACION_ES_API_KEY as string;
  const notificacionSweApiUrl = process.env.REACT_APP_SM_NOTIFICACION_SWE_API_URL as string;
  const notificacionSweApiKey = process.env.REACT_APP_SM_NOTIFICACION_SWE_API_KEY as string;
  const isAdmin = useMemo(() => {
    try {
      const raw = process.env.REACT_APP_SM_ACCESAD_LIST;
      if (!raw) return false;
      const list = JSON.parse(raw);
      if (Array.isArray(list)) {
        return list.map((email: string) => email.toLowerCase().trim()).includes(userEmail);
      }
    } catch {
      return false;
    }
    return false;
  }, [userEmail]);
  const matchedService = useMemo(() => {
    const normalizedServiceId = normalizeComparableText(serviceId);
    const normalizedServiceName = normalizeComparableText(serviceName);

    if (normalizedServiceId) {
      const matchById = servicesData.find(
        (row) => normalizeComparableText(getServiceId(row)) === normalizedServiceId
      );
      if (matchById) return matchById;
    }

    if (!normalizedServiceName) return null;

    return (
      servicesData.find(
        (row) =>
          normalizeComparableText(getServiceName(row)) === normalizedServiceName &&
          !/^agt[a-z0-9_-]+$/i.test(getServiceId(row).trim())
      ) ||
      servicesData.find(
        (row) => normalizeComparableText(getServiceName(row)) === normalizedServiceName
      ) ||
      null
    );
  }, [servicesData, serviceId, serviceName]);
  const effectiveServiceId = useMemo(
    () => serviceId || (matchedService ? getServiceId(matchedService) : ''),
    [serviceId, matchedService]
  );
  const isGptCeuServiceSelected = useMemo(() => {
    const normalizeFunctionality = (value: string) =>
      normalizeFuncionalidadValue(value).toLowerCase().replace(/[\s_-]+/g, '');
    const functionality = matchedService ? getFieldValue(matchedService, ['Funcionalidad']) : '';
    const routeIdentifiesGptCeu = [serviceName, serviceId].some(
      (value) => normalizeFunctionality(value) === 'gptceu'
    );

    return routeIdentifiesGptCeu || normalizeFunctionality(functionality) === 'gptceu';
  }, [matchedService, serviceName, serviceId]);
  const isUtdServiceSelected = useMemo(
    () => normalizeComparableText(effectiveServiceId) === 'utd',
    [effectiveServiceId]
  );
  const isBecasColabServiceSelected = useMemo(
    () => normalizeComparableText(effectiveServiceId) === 'becascolab',
    [effectiveServiceId]
  );
  const isImpagosUchService = useMemo(
    () =>
      [serviceName, effectiveServiceId].some(
        (value) => normalizeComparableText(value) === normalizeComparableText(IMPAGOS_UCH_SERVICE_ID)
      ),
    [serviceName, effectiveServiceId]
  );
  const isAccidentePracticasService = useMemo(
    () => normalizeComparableText(effectiveServiceId) === 'accidentepracticas',
    [effectiveServiceId]
  );
  const isItServiceSid = useMemo(
    () => normalizeComparableText(effectiveServiceId) === 'it',
    [effectiveServiceId]
  );
  const isAsistenciaItService = useMemo(
    () =>
      normalizeComparableText(effectiveServiceId) === 'it' ||
      normalizeComparableText(serviceName) === normalizeComparableText('Asistencia IT'),
    [serviceName, effectiveServiceId]
  );
  const serviceTableSource = useMemo<ServiceTableSource | null>(() => {
    if (isUtdServiceSelected) return 'utd';
    if (isBecasColabServiceSelected) return 'becasColab';
    return null;
  }, [isUtdServiceSelected, isBecasColabServiceSelected]);
  const serviceTableTitle = serviceTableSource === 'becasColab' ? 'Becas colaboración' : 'Procesos UTD';
  const serviceTableItemName = serviceTableSource === 'becasColab' ? 'beca' : 'proceso';
  const serviceTableFileName = serviceTableSource === 'becasColab' ? BECAS_COLAB_FILE_NAME : UTD_PROCESS_FILE_NAME;
  const serviceTableContainerName = serviceTableSource === 'becasColab' ? 'promptsprocesos' : process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const domainInstructions = instructionFiles.some((file) => file.almacenamiento === 'dominio');
  const canPublishPrompt = useMemo(
    () => !domainInstructions && Boolean(matchedService && isImplementedService(getServiceStatus(matchedService))),
    [matchedService, domainInstructions]
  );
  const deployedPromptStatus = useMemo(() => {
    if (!promptFileName) return 'Sin prompt configurado';
    if (checkingDeployedPrompt) return 'Comprobando producción...';
    if (deployedPromptError) return 'No se pudo comprobar producción';
    if (promptLastModifiedMs === null) return 'Sin fecha de desarrollo';
    if (deployedPromptLastModifiedMs === null) return 'Sin publicar en producción';
    return deployedPromptLastModifiedMs >= promptLastModifiedMs
      ? 'Producción actualizada'
      : 'Cambios pendientes de publicar';
  }, [
    promptFileName,
    checkingDeployedPrompt,
    deployedPromptError,
    promptLastModifiedMs,
    deployedPromptLastModifiedMs,
  ]);
  const isPromptPendingPublish =
    canPublishPrompt &&
    !checkingDeployedPrompt &&
    !deployedPromptError &&
    Boolean(promptFileName) &&
    promptLastModifiedMs !== null &&
    (deployedPromptLastModifiedMs === null || deployedPromptLastModifiedMs < promptLastModifiedMs);
  const isPromptOutdated =
    canPublishPrompt &&
    !checkingDeployedPrompt &&
    !deployedPromptError &&
    Boolean(promptFileName) &&
    promptLastModifiedMs !== null &&
    deployedPromptLastModifiedMs !== null &&
    deployedPromptLastModifiedMs < promptLastModifiedMs;
  const isPromptPublished =
    canPublishPrompt &&
    !checkingDeployedPrompt &&
    !deployedPromptError &&
    Boolean(promptFileName) &&
    promptLastModifiedMs !== null &&
    deployedPromptLastModifiedMs !== null &&
    deployedPromptLastModifiedMs >= promptLastModifiedMs;
  const hasServiceAccess = useMemo(() => {
    if (isAdmin) return Boolean(matchedService || serviceIdentifier);
    if (!matchedService) return false;

    const responsables = extractEmails(matchedService['Responsable servicio']);
    const tecnicos = extractEmails(matchedService['Técnico']);
    const revisores = extractEmails(matchedService['Revisor/a']);

    return (
      responsables.includes(userEmail) ||
      tecnicos.includes(userEmail) ||
      revisores.includes(userEmail)
    );
  }, [isAdmin, matchedService, serviceIdentifier, userEmail]);

  useEffect(() => {
    document.documentElement.lang = 'es';
  }, []);

  useEffect(() => {
    const fetchVars = async () => {
      try {
        setLoadingVars(true);
        setVarError(null);
        
        // 1. Cargar la configuración JSON del servicio
        const configUrl = await getBlobServiceConfigUrl(serviceIdentifier);
        const configRes = await fetch(configUrl);
        let variablesGuardadas: Record<string, string> = {};
        let customVarsFromConfig: PromptVar[] = [];
        if (configRes.ok) {
          const configData = await configRes.json();
          variablesGuardadas = configData?.Variables || {};
          customVarsFromConfig = parseCustomPromptVars(configData?.VariablesCustomDefinitions);
          setServiceDocuments(Array.isArray(configData?.Documentos) ? configData.Documentos : []);
        } else {
          setServiceDocuments([]);
        }

        // 2. Cargar el CSV de variables disponibles
        const csvUrl = await getBlobVariablesCsvUrl();
        const csvRes = await fetch(csvUrl);
        if (!csvRes.ok) throw new Error('Error al obtener el archivo de variables CSV.');
        
        const csvText = await csvRes.text();
        
        // 3. Parsear el CSV
        Papa.parse(csvText, {
          header: true,
          delimiter: ';',
          skipEmptyLines: true,
          complete: (results) => {
            const defs = results.data as Array<Record<string, unknown>>;
            const mergedByPrompt = new Map<string, PromptVar>();

            defs.forEach((row) => {
              const prompt = String(
                row?.Nombre_variable_en_prompt ??
                row?.['\uFEFFNombre_variable_en_prompt'] ??
                ''
              ).trim();
              const code = String(row?.Nombre_variable_en_codigo || '').trim();
              if (!prompt || !code) return;
              mergedByPrompt.set(normalizeComparableText(prompt), {
                prompt,
                code,
                descripcion: String(row?.Decripcion ?? row?.Descripcion ?? '').trim(),
                selected: Object.prototype.hasOwnProperty.call(variablesGuardadas, prompt),
                source: 'csv',
              });
            });

            customVarsFromConfig.forEach((customVar) => {
              const normalizedPrompt = normalizeComparableText(customVar.prompt);
              if (!normalizedPrompt || mergedByPrompt.has(normalizedPrompt)) return;
              mergedByPrompt.set(normalizedPrompt, {
                ...customVar,
                selected: Object.prototype.hasOwnProperty.call(variablesGuardadas, customVar.prompt),
                source: 'custom',
              });
            });

            Object.entries(variablesGuardadas).forEach(([prompt, code]) => {
              const normalizedPrompt = normalizeComparableText(prompt);
              if (!normalizedPrompt || mergedByPrompt.has(normalizedPrompt)) return;
              mergedByPrompt.set(normalizedPrompt, {
                prompt,
                code,
                descripcion: 'Variable personalizada',
                selected: true,
                source: 'custom',
              });
            });

            setVars(Array.from(mergedByPrompt.values()));
          },
          error: (error: any) => {
            throw new Error(`Error parseando CSV: ${error.message}`);
          }
        });

      } catch (err: any) {
        setVarError(err.message || 'Error al cargar las variables.');
      } finally {
        setLoadingVars(false);
      }
    };

    if (serviceIdentifier && hasServiceAccess) fetchVars();
  }, [serviceIdentifier, hasServiceAccess]);

  useEffect(() => {
    const fetchUtdProcesses = async () => {
      if (!serviceTableSource) return;
      try {
        setLoadingUtdProcesses(true);
        setUtdProcessError(null);
        setUtdProcessSuccess(null);

        const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
        const containerName = serviceTableContainerName;

        if (!connectionString || !containerName) {
          throw new Error(
            `Faltan variables de entorno para cargar ${serviceTableFileName} desde el blob.`
          );
        }

        const readUrl = await getBlobUrlByConnectionString(
          connectionString,
          containerName,
          serviceTableFileName,
          'r'
        );
        const response = await fetch(readUrl);

        if (!response.ok && response.status !== 404) {
          throw new Error(`No se pudo cargar ${serviceTableFileName} (${response.status}).`);
        }

        let headers = getDefaultServiceTableHeaders(serviceTableSource);
        let rows: UtdProcessRow[] = [];

        if (response.status !== 404) {
          if (serviceTableSource === 'becasColab') {
            const jsonData = await response.json();
            if (!Array.isArray(jsonData)) {
              throw new Error(`${BECAS_COLAB_FILE_NAME} no contiene una lista de becas válida.`);
            }

            const discoveredHeaders = new Set(headers);
            jsonData.forEach((row) => {
              if (row && typeof row === 'object') {
                Object.keys(row as Record<string, unknown>).forEach((key) => discoveredHeaders.add(key));
              }
            });
            headers = Array.from(discoveredHeaders);

            rows = jsonData.map((row) => {
              const safeRow = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
              const formattedRow: UtdProcessRow = {};
              headers.forEach((header) => {
                formattedRow[header] = stringifyBecasCell(header, safeRow[header]);
              });
              return formattedRow;
            });
          } else {
            const csvText = await response.text();
            const parsed = Papa.parse<Record<string, unknown>>(csvText, {
              header: true,
              delimiter: ';',
              skipEmptyLines: true,
            });

            if (parsed.errors.length > 0) {
              throw new Error(parsed.errors[0].message || `Error al parsear ${UTD_PROCESS_FILE_NAME}.`);
            }

            headers = (parsed.meta.fields || []).filter(Boolean);
            if (!headers.length) {
              headers = [...UTD_PROCESS_DEFAULT_HEADERS];
            }

            rows = (parsed.data || []).map((row) => {
              const formattedRow: UtdProcessRow = {};
              headers.forEach((header) => {
                const cell = row?.[header];
                formattedRow[header] = typeof cell === 'string' ? cell : cell == null ? '' : String(cell);
              });
              return formattedRow;
            });
          }
        }

        setUtdProcessHeaders(headers);
        setUtdProcessRows(rows);
        setUtdProcessSnapshot(serializeUtdProcessSnapshot(headers, rows));
      } catch (err: any) {
        setUtdProcessHeaders(getDefaultServiceTableHeaders(serviceTableSource));
        setUtdProcessRows([]);
        setUtdProcessError(err.message || `No se pudo cargar la tabla de ${serviceTableTitle}.`);
      } finally {
        setLoadingUtdProcesses(false);
      }
    };

    if (serviceTableSource && hasServiceAccess) {
      void fetchUtdProcesses();
      return;
    }

    setUtdProcessHeaders([]);
    setUtdProcessRows([]);
    setUtdProcessError(null);
    setUtdProcessSuccess(null);
    setLoadingUtdProcesses(false);
    setUtdProcessSnapshot(serializeUtdProcessSnapshot([], []));
  }, [serviceTableSource, serviceTableFileName, serviceTableContainerName, serviceTableTitle, hasServiceAccess]);

  useEffect(() => {
    let cancelled = false;

    const fetchTextBlob = async () => {
      try {
        setLoadingText(true);
        setTextError(null);
        setPromptFileName('');
        setInstructionFiles([]);
        setPromptLastModifiedMs(null);
        setDeployedPromptLastModifiedMs(null);
        setDeployedPromptError(null);

        const configUrl = await getBlobServiceConfigUrl(effectiveServiceId || serviceIdentifier);
        const configRes = await fetch(configUrl, { cache: 'no-store' });
        if (!configRes.ok) {
          throw new Error(`No se pudo cargar la configuración ${effectiveServiceId || serviceIdentifier}.json.`);
        }

        const configData = await configRes.json();
        if (cancelled) return;
        // El archivo principal equivale al "prompt" de siempre; sin "instrucciones" es el único.
        const files = parseInstruccionesServicio(configData, effectiveServiceId || serviceIdentifier);
        const promptFromConfig = files[0]?.ruta || '';
        if (!promptFromConfig) {
          throw new Error(`La configuración ${serviceIdentifier}.json no contiene el campo "prompt".`);
        }

        setInstructionFiles(files);
        setPromptFileName(promptFromConfig);

        const snapshot = await readInstruction(files[0], instructionsToken);
        if (cancelled) return;
        setEditablePromptText(snapshot.contenido);
        setPromptLastModifiedMs(snapshot.lastModified ? Date.parse(snapshot.lastModified) : null);
        if (!snapshot.existe) {
          setTextError(`No existe el archivo "${files[0].nombre}". Puedes crearlo y guardarlo.`);
        }
      } catch (err: any) {
        if (!cancelled) {
          setTextError(err.message || 'No se pudo cargar el archivo TXT del servicio.');
        }
      } finally {
        if (!cancelled) setLoadingText(false);
      }
    };

    if (serviceIdentifier && hasServiceAccess) fetchTextBlob();

    return () => {
      cancelled = true;
    };
  }, [
    effectiveServiceId,
    serviceIdentifier,
    hasServiceAccess,
    instructionsToken,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchDeployedPrompt = async () => {
      const fileName = promptFileName.trim();
      if (!fileName || !canPublishPrompt || promptLastModifiedMs === null) {
        setDeployedPromptLastModifiedMs(null);
        setDeployedPromptError(null);
        setCheckingDeployedPrompt(false);
        return;
      }

      const primaryBlobConnectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
      const secondaryBlobConnectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
      const promptsContainerName = (process.env.REACT_APP_SM_PROMPTS_CONTAINER_NAME as string) || 'promptsprocesos';
      const promptsDeployedFolderName = (process.env.REACT_APP_SM_PROMPTS_DEPLOYED_FOLDER_NAME as string) || 'desplegado';
      const connectionStrings = [primaryBlobConnectionString, secondaryBlobConnectionString].filter(Boolean);

      if (!connectionStrings.length) {
        setDeployedPromptLastModifiedMs(null);
        setDeployedPromptError('Faltan connection strings de blob para comprobar el prompt desplegado.');
        return;
      }

      setCheckingDeployedPrompt(true);
      setDeployedPromptError(null);

      try {
        const deployedPromptBlob = buildDeployedPromptBlobPath(promptsDeployedFolderName, fileName);
        for (const connectionString of connectionStrings) {
          const deployedPromptReadUrl = await getBlobUrlByConnectionString(
            connectionString,
            promptsContainerName,
            deployedPromptBlob,
            'r'
          );
          const deployedRes = await fetch(deployedPromptReadUrl);
          if (deployedRes.ok) {
            const lastModifiedMs = getResponseLastModifiedMs(deployedRes);
            if (lastModifiedMs === null) {
              throw new Error(`No se pudo obtener la fecha de última modificación de ${deployedPromptBlob}.`);
            }
            if (!cancelled) setDeployedPromptLastModifiedMs(lastModifiedMs);
            return;
          }
          if (deployedRes.status !== 404) {
            throw new Error(`No se pudo comprobar ${deployedPromptBlob} (${deployedRes.status}).`);
          }
        }
        if (!cancelled) setDeployedPromptLastModifiedMs(null);
      } catch (err: any) {
        if (!cancelled) {
          setDeployedPromptLastModifiedMs(null);
          setDeployedPromptError(err.message || 'No se pudo comprobar el prompt desplegado.');
        }
      } finally {
        if (!cancelled) setCheckingDeployedPrompt(false);
      }
    };

    void fetchDeployedPrompt();

    return () => {
      cancelled = true;
    };
  }, [promptFileName, promptLastModifiedMs, canPublishPrompt]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoadingConversations(true);
        setConversationError(null);
        const selectedUrl = conversationBackend === 'ES' ? conversationsEsApiUrl : conversationsSweApiUrl;
        const selectedKey = conversationBackend === 'ES' ? conversationsEsApiKey : conversationsSweApiKey;

        if (!selectedUrl || !selectedKey) {
          throw new Error(`Faltan variables de entorno para conversaciones (${conversationBackend}).`);
        }
        const endpoint = `${selectedUrl}?code=${selectedKey}&servicio=${encodeURIComponent(serviceName)}&conversaciones=True`;
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error(`Error al cargar conversaciones (${res.status}).`);
        const parsed = await res.json();
        setConversationsRaw(parsed || {});
      } catch (err: any) {
        setConversationError(err.message || 'No se pudieron cargar las conversaciones.');
      } finally {
        setLoadingConversations(false);
      }
    };
    if (serviceName && hasServiceAccess) fetchConversations();
  }, [serviceName, hasServiceAccess, conversationBackend, conversationsEsApiUrl, conversationsEsApiKey, conversationsSweApiUrl, conversationsSweApiKey, conversationRefreshToken]);

  const toggleVar = (promptName: string) => {
    setVars((prev) => prev.map((v) => (v.prompt === promptName ? { ...v, selected: !v.selected } : v)));
    if (varError) setVarError(null);
  };

  const readVariablesCsvFromBlob = async (): Promise<PromptVarCsvDefinition[]> => {
    const variablesCsvReadUrl = await getBlobVariablesCsvUrl();
    const csvRes = await fetch(variablesCsvReadUrl);
    if (!csvRes.ok) {
      throw new Error('No se pudo leer lista_variables_servicios.csv.');
    }
    const csvText = await csvRes.text();
    return parseVariablesCsvDefinitions(csvText);
  };

  const writeVariablesCsvToBlob = async (definitions: PromptVarCsvDefinition[]) => {
    const updatedCsv = buildVariablesCsv(definitions);
    const variablesCsvWriteUrl = await getBlobVariablesCsvWriteUrl();
    const primaryCsvWriteRes = await fetch(variablesCsvWriteUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'text/csv; charset=utf-8',
      },
      body: updatedCsv,
    });

    if (!primaryCsvWriteRes.ok) {
      throw new Error('No se pudo guardar lista_variables_servicios.csv en el blob principal.');
    }

    try {
      const secondaryVariablesCsvWriteUrl = await getSecondaryBlobVariablesCsvWriteUrl();
      await fetch(secondaryVariablesCsvWriteUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'text/csv; charset=utf-8',
        },
        body: updatedCsv,
      });
    } catch (e) {
      console.warn('No se pudo guardar lista_variables_servicios.csv en el blob secundario:', e);
    }
  };

  const resetNewVarForm = () => {
    setNewVarPrompt('');
    setNewVarDescription('');
    setNewVarCode('');
    setNewVarError(null);
  };

  const resetEditVarForm = () => {
    setEditingVarPrompt(null);
    setEditVarPrompt('');
    setEditVarDescription('');
    setEditVarCode('');
    setEditVarError(null);
  };

  const handleOpenEditVarForm = (variable: PromptVar) => {
    setEditingVarPrompt(variable.prompt);
    setEditVarPrompt(variable.prompt);
    setEditVarDescription(variable.descripcion);
    setEditVarCode(variable.code);
    setEditVarError(null);
  };

  const handleDeleteVarFromCsv = async (variable: PromptVar) => {
    const confirmed = window.confirm(`¿Quieres eliminar la variable "${variable.prompt}" del CSV global?`);
    if (!confirmed) return;

    const normalizedPrompt = normalizeComparableText(variable.prompt);
    setDeletingVarPrompt(variable.prompt);
    setVarError(null);

    try {
      const existingDefinitions = await readVariablesCsvFromBlob();
      const updatedDefinitions = existingDefinitions.filter(
        (item) => normalizeComparableText(item.Nombre_variable_en_prompt) !== normalizedPrompt
      );

      await writeVariablesCsvToBlob(updatedDefinitions);

      setVars((prev) =>
        prev.filter((item) => normalizeComparableText(item.prompt) !== normalizedPrompt)
      );
    } catch (err: any) {
      setVarError(err.message || 'No se pudo eliminar la variable del CSV.');
    } finally {
      setDeletingVarPrompt(null);
    }
  };

  const handleSaveVarEdition = async () => {
    if (!editingVarPrompt) return;

    const originalPrompt = editingVarPrompt;
    const nextPrompt = editVarPrompt.trim();
    const nextDescripcion = editVarDescription.trim();
    const nextCode = editVarCode.trim();

    if (!nextPrompt || !nextDescripcion || !nextCode) {
      setEditVarError('Completa nombre visible, descripción y nombre en código.');
      return;
    }

    const duplicatedPrompt = vars.some(
      (item) =>
        normalizeComparableText(item.prompt) === normalizeComparableText(nextPrompt) &&
        normalizeComparableText(item.prompt) !== normalizeComparableText(originalPrompt)
    );
    if (duplicatedPrompt) {
      setEditVarError('Ya existe otra variable con ese nombre visible.');
      return;
    }

    setEditingVarSaving(true);
    setEditVarError(null);
    setVarError(null);

    try {
      const originalNormalizedPrompt = normalizeComparableText(originalPrompt);
      const updatedDefinition: PromptVarCsvDefinition = {
        Nombre_variable_en_prompt: nextPrompt,
        Nombre_variable_en_codigo: nextCode,
        Decripcion: nextDescripcion,
      };

      const existingDefinitions = await readVariablesCsvFromBlob();
      const definitionsByPrompt = new Map<string, PromptVarCsvDefinition>();
      existingDefinitions.forEach((item) => {
        definitionsByPrompt.set(normalizeComparableText(item.Nombre_variable_en_prompt), item);
      });

      definitionsByPrompt.delete(originalNormalizedPrompt);
      definitionsByPrompt.set(normalizeComparableText(nextPrompt), updatedDefinition);

      await writeVariablesCsvToBlob(Array.from(definitionsByPrompt.values()));

      setVars((prev) =>
        prev.map((item) => {
          if (normalizeComparableText(item.prompt) !== originalNormalizedPrompt) return item;
          return {
            ...item,
            prompt: nextPrompt,
            descripcion: nextDescripcion,
            code: nextCode,
            source: 'csv',
          };
        })
      );

      resetEditVarForm();
    } catch (err: any) {
      setEditVarError(err.message || 'No se pudo actualizar la variable en el CSV.');
    } finally {
      setEditingVarSaving(false);
    }
  };

  const handleToggleCreateVarForm = () => {
    setShowCreateVarForm((prev) => {
      const next = !prev;
      if (!next) resetNewVarForm();
      return next;
    });
  };

  const handleAddCustomVar = () => {
    const prompt = newVarPrompt.trim();
    const descripcion = newVarDescription.trim();
    const code = newVarCode.trim();

    if (!prompt || !descripcion || !code) {
      setNewVarError('Completa nombre visible, descripción y nombre en código.');
      return;
    }

    const duplicatedPrompt = vars.some(
      (v) => normalizeComparableText(v.prompt) === normalizeComparableText(prompt)
    );
    if (duplicatedPrompt) {
      setNewVarError('Ya existe una variable con ese nombre visible.');
      return;
    }

    setVars((prev) => [
      ...prev,
      {
        prompt,
        descripcion,
        code,
        selected: true,
        source: 'custom',
      },
    ]);
    setShowCreateVarForm(false);
    resetNewVarForm();
    if (varError) setVarError(null);
  };


  const processedConversations = useMemo(() => {
    if (!conversationsRaw) return [];
    if (Array.isArray(conversationsRaw) || typeof conversationsRaw !== 'object') return [];
    const list: ProcessedConversation[] = [];
    for (const [userId, value] of Object.entries(conversationsRaw)) {
      if (userId === SURVEYS_SENT_KEY) continue;
      if (isMessagesArray(value)) {
        const processed = processConversationByDay(
          value,
          serviceName,
          userId,
          '0',
          !isGptCeuServiceSelected
        );
        list.push(...processed);
        continue;
      }
      if (value && typeof value === 'object') {
        let idx = 0;
        for (const maybeArr of Object.values(value as Record<string, unknown>)) {
          if (!isMessagesArray(maybeArr)) continue;
          const processed = processConversationByDay(
            maybeArr,
            serviceName,
            userId,
            String(idx),
            !isGptCeuServiceSelected
          );
          list.push(...processed);
          idx += 1;
        }
      }
    }
    return list;
  }, [conversationsRaw, serviceName, isGptCeuServiceSelected]);

  const surveysSent = useMemo<Record<string, SurveySentInfo>>(() => {
    const fromServer =
      conversationsRaw && typeof conversationsRaw === 'object' && !Array.isArray(conversationsRaw)
        ? ((conversationsRaw as Record<string, unknown>)[SURVEYS_SENT_KEY] as Record<string, SurveySentInfo> | undefined)
        : undefined;
    return { ...(fromServer || {}), ...surveySentLocal };
  }, [conversationsRaw, surveySentLocal]);

  const rangeLabel = useMemo(() => {
    if (!appliedDateRange) return '-';
    const start = new Date(appliedDateRange.startMs).toLocaleDateString(DEFAULT_LANG);
    const end = new Date(appliedDateRange.endMs).toLocaleDateString(DEFAULT_LANG);
    return `${start} - ${end}`;
  }, [appliedDateRange]);

  const conversationStats = useMemo(() => {
    let earliest = Number.MAX_SAFE_INTEGER;
    for (const conv of processedConversations) {
      if (conv.maxTimestampMs > 0 && conv.maxTimestampMs < earliest) earliest = conv.maxTimestampMs;
    }
    return {
      totalGlobal: processedConversations.length,
    };
  }, [processedConversations]);

  const filteredGroupedConversations = useMemo(() => {
    if (!appliedDateRange) return { grouped: {} as Record<string, ProcessedConversation[]>, sortedKeys: [] as string[] };
    const search = normalizeText(searchTerm);
    const grouped: Record<string, ProcessedConversation[]> = {};

    for (const conv of processedConversations) {
      if (conv.maxTimestampMs < appliedDateRange.startMs || conv.maxTimestampMs > appliedDateRange.endMs) continue;
      if (search && !normalizeText(conv.userDisplay).includes(search)) continue;
      // Una conversación con respuestas buenas y malas aparece en ambos filtros
      if (ratingFilter === 'valoradas' && !conv.messages.some((m) => m.valoracion === 'buena' || m.valoracion === 'mala')) continue;
      if ((ratingFilter === 'buena' || ratingFilter === 'mala') && !conv.messages.some((m) => m.valoracion === ratingFilter)) continue;
      if (channelFilter !== 'todas' && !conv.canales.includes(channelFilter)) continue;
      if (
        isGptCeuServiceSelected &&
        ((hidePhoneConversations && isPhoneUserId(conv.userId)) ||
          (hideEmailConversations && isEmailUserId(conv.userId)))
      ) {
        continue;
      }

      const key = conv.dateKey;
      grouped[key] ??= [];
      grouped[key].push(conv);
    }

    Object.values(grouped).forEach((conversations) => {
      conversations.sort((a, b) => {
        const timestampDifference = b.maxTimestampMs - a.maxTimestampMs;
        if (timestampDifference !== 0) return timestampDifference;
        return a.id.localeCompare(b.id);
      });
    });

    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      const ta = Date.parse(a.split('/').reverse().join('-'));
      const tb = Date.parse(b.split('/').reverse().join('-'));
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });
    return { grouped, sortedKeys };
  }, [
    processedConversations,
    searchTerm,
    ratingFilter,
    channelFilter,
    appliedDateRange,
    isGptCeuServiceSelected,
    hidePhoneConversations,
    hideEmailConversations,
  ]);

  const visibleConversationCount = useMemo(
    () =>
      filteredGroupedConversations.sortedKeys.reduce(
        (acc, key) => acc + filteredGroupedConversations.grouped[key].length,
        0
      ),
    [filteredGroupedConversations]
  );

  const visibleServiceDocuments = useMemo(
    () => (showAllDocuments ? serviceDocuments : serviceDocuments.slice(0, VISIBLE_DOCUMENTS_LIMIT)),
    [serviceDocuments, showAllDocuments]
  );
  const hasUtdProcessChanges = useMemo(
    () => serializeUtdProcessSnapshot(utdProcessHeaders, utdProcessRows) !== utdProcessSnapshot,
    [utdProcessHeaders, utdProcessRows, utdProcessSnapshot]
  );
  const utdPrimaryHeader = useMemo(() => {
    if (!utdProcessHeaders.length) return '';
    const preferred = utdProcessHeaders.find((h) => {
      const normalizedHeader = normalizeComparableText(h);
      return serviceTableSource === 'becasColab'
        ? normalizedHeader === 'title'
        : normalizedHeader === 'nombre proceso';
    });
    return preferred || utdProcessHeaders[1] || utdProcessHeaders[0];
  }, [utdProcessHeaders, serviceTableSource]);
  const utdSecondaryHeader = useMemo(
    () =>
      utdProcessHeaders.find((h) => {
        const n = normalizeComparableText(h);
        if (serviceTableSource === 'becasColab') {
          return n === 'descripcion' || n === 'campus' || n === 'requisitos';
        }
        return n === 'descripcion' || n === 'centro' || n === 'rol';
      }) || '',
    [utdProcessHeaders, serviceTableSource]
  );
  const utdIdHeader = useMemo(
    () =>
      utdProcessHeaders.find((h) => {
        const normalizedHeader = normalizeComparableText(h);
        return serviceTableSource === 'becasColab'
          ? normalizedHeader === 'id_beca' 
          : normalizedHeader === 'id';
      }) || '',
    [utdProcessHeaders, serviceTableSource]
  );
  const filteredUtdRowIndexes = useMemo(() => {
    const search = normalizeText(utdSearch);
    const indexes = utdProcessRows.map((_, i) => i);
    if (!search) return indexes;
    return indexes.filter((i) => {
      const row = utdProcessRows[i];
      return utdProcessHeaders.some((h) =>
        normalizeText(row[h] || '').includes(search)
      );
    });
  }, [utdProcessRows, utdProcessHeaders, utdSearch]);
  const utdEditingRow =
    utdEditorRowIndex !== null ? utdProcessRows[utdEditorRowIndex] : null;
  const becasUserFieldHeaders = useMemo(
    () => {
      if (serviceTableSource !== 'becasColab') return [];
      return BECAS_USER_FIELDS.flatMap((field) => {
        const header = utdProcessHeaders.find(
          (h) => normalizeComparableText(h) === normalizeComparableText(field.header)
        );
        return header ? [{ ...field, header }] : [];
      });
    },
    [serviceTableSource, utdProcessHeaders]
  );
  const becasUserCount = useMemo(
    () => {
      if (!utdEditingRow || !becasUserFieldHeaders.length) return 0;
      return becasUserFieldHeaders.reduce(
        (max, field) => Math.max(max, splitBecasUserValues(utdEditingRow[field.header] || '').length),
        0
      );
    },
    [utdEditingRow, becasUserFieldHeaders]
  );

  useEffect(() => {
    if (serviceDocuments.length <= VISIBLE_DOCUMENTS_LIMIT && showAllDocuments) {
      setShowAllDocuments(false);
    }
  }, [serviceDocuments, showAllDocuments]);

  const toggleConversation = (id: string) => {
    setExpandedConversations((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyConversationEmail = async (conv: ProcessedConversation) => {
    const email = (conv.userId || '').trim();
    if (!isEmailUserId(email)) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopiedConversationId(conv.id);
      window.setTimeout(() => {
        setCopiedConversationId((prev) => (prev === conv.id ? null : prev));
      }, 1500);
    } catch (err) {
      console.error('No se pudo copiar el correo al portapapeles', err);
    }
  };

  const handleImpagosDraftChange = (conversationId: string, value: string) => {
    setImpagosMessageDrafts((prev) => ({ ...prev, [conversationId]: value }));
    setImpagosMessageFeedback((prev) => {
      if (!prev[conversationId]) return prev;
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
  };

  const handleSendImpagosMessage = async (conv: ProcessedConversation) => {
    const message = (impagosMessageDrafts[conv.id] || '').trim();
    if (!message) {
      setImpagosMessageFeedback((prev) => ({
        ...prev,
        [conv.id]: { type: 'error', text: 'Escribe un mensaje antes de enviarlo.' },
      }));
      return;
    }

    const rawUserId = (conv.userId || '').trim();
    const phoneCandidate = rawUserId.replace(/[\s-]/g, '');
    const isPhone = /^\+?\d{6,}$/.test(phoneCandidate);
    const payload = {
      correo: isPhone ? '' : rawUserId,
      telefono: isPhone ? phoneCandidate : '',
      servicio: IMPAGOS_UCH_SERVICE_ID,
      mensaje: message,
      get_user: 'False',
    };

    setImpagosSendingMessages((prev) => ({ ...prev, [conv.id]: true }));
    setImpagosMessageFeedback((prev) => {
      const next = { ...prev };
      delete next[conv.id];
      return next;
    });

    try {
      const response = await fetch(IMPAGOS_UCH_SEND_MESSAGE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `No se pudo enviar el mensaje (${response.status}).`);
      }

      setImpagosMessageDrafts((prev) => ({ ...prev, [conv.id]: '' }));
      setImpagosMessageFeedback((prev) => ({
        ...prev,
        [conv.id]: { type: 'success', text: responseText || 'Mensaje enviado correctamente.' },
      }));
      setConversationRefreshToken((prev) => prev + 1);
    } catch (err: any) {
      setImpagosMessageFeedback((prev) => ({
        ...prev,
        [conv.id]: { type: 'error', text: err.message || 'No se pudo enviar el mensaje.' },
      }));
    } finally {
      setImpagosSendingMessages((prev) => ({ ...prev, [conv.id]: false }));
    }
  };

  // Pide al backend que envíe la plantilla de encuesta de 24h por WhatsApp; la respuesta del usuario
  // se registra en satisfaccion_asistentes.json y en su log de conversación
  // Encuesta de 24h ya enviada a este usuario en las últimas 24 horas (según el backend o enviada en esta sesión)
  const getSurveySentInfo = (userId: string): SurveySentInfo | null => {
    const id = (userId || '').trim();
    const info = surveysSent[id] || surveysSent[id.replace(/^\+/, '')];
    if (!info) return null;
    const sentMs = Number(info.timestamp) * 1000;
    return Number.isFinite(sentMs) && Date.now() - sentMs < SURVEY_COOLDOWN_MS ? info : null;
  };

  const handleSendWhatsappSurvey = async (conv: ProcessedConversation) => {
    const userKey = conv.userId;
    // Evita dobles clics: el estado de React no se actualiza a tiempo si se pulsa varias veces seguidas
    if (surveyInFlightRef.current.has(userKey) || getSurveySentInfo(userKey)) return;

    const selectedUrl = conversationBackend === 'ES' ? notificacionEsApiUrl : notificacionSweApiUrl;
    const selectedKey = conversationBackend === 'ES' ? notificacionEsApiKey : notificacionSweApiKey;
    const setFeedback = (type: 'success' | 'error', text: string) =>
      setSurveyFeedback((prev) => ({ ...prev, [userKey]: { type, text } }));

    if (!selectedUrl || !selectedKey) {
      setFeedback('error', `Faltan variables de entorno para enviar la encuesta (${conversationBackend}).`);
      return;
    }

    const rawUserId = (conv.userId || '').trim();
    const phoneCandidate = rawUserId.replace(/[\s-]/g, '');
    const isPhone = /^\+?\d{6,}$/.test(phoneCandidate);

    surveyInFlightRef.current.add(userKey);
    setSurveySending((prev) => ({ ...prev, [userKey]: true }));
    setSurveyFeedback((prev) => {
      const next = { ...prev };
      delete next[userKey];
      return next;
    });
    try {
      const response = await fetch(`${selectedUrl}?code=${selectedKey}&accion=encuesta_whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          correo: isPhone ? '' : rawUserId,
          telefono: isPhone ? phoneCandidate : '',
          servicio: serviceName,
        }),
      });
      const data = await response.json().catch(() => null);
      // Enviada ahora o ya enviada en las últimas 24h: en ambos casos se bloquea el botón para ese usuario
      if (data?.ok || data?.ya_enviada) {
        setSurveySentLocal((prev) => ({
          ...prev,
          [userKey]: {
            fecha: data.fecha || new Date().toLocaleString(DEFAULT_LANG),
            timestamp: Number(data.timestamp) || Date.now() / 1000,
            servicio: serviceName,
          },
        }));
      }
      if (!response.ok || !data?.ok) {
        throw new Error(data?.mensaje || `No se pudo enviar la encuesta (${response.status}).`);
      }
      setFeedback('success', data.mensaje || 'Encuesta enviada por WhatsApp.');
    } catch (err: any) {
      setFeedback('error', err.message || 'No se pudo enviar la encuesta.');
    } finally {
      surveyInFlightRef.current.delete(userKey);
      setSurveySending((prev) => ({ ...prev, [userKey]: false }));
    }
  };

  const handleSearchResetMfaJira = async () => {
    const idIncidencia = resetMfaJiraId.trim();
    if (!idIncidencia || resetMfaSearching) return;
    if (!notificacionEsApiUrl || !notificacionEsApiKey) {
      setResetMfaFeedback({ type: 'error', text: 'Faltan variables de entorno para consultar la incidencia (ES).' });
      return;
    }

    setResetMfaSearching(true);
    setResetMfaFeedback(null);
    setResetMfaIncidencia(null);
    try {
      const searchResponse = await fetch(`${notificacionEsApiUrl}?code=${notificacionEsApiKey}&accion=search_id_jira`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_incidencia: idIncidencia }),
      });
      const searchData = await searchResponse.json().catch(() => null);
      if (searchData?.incidencia) setResetMfaIncidencia(searchData.incidencia);
      if (!searchResponse.ok || !searchData?.ok || !searchData.incidencia) {
        throw new Error(searchData?.mensaje || `No se pudo consultar la incidencia (${searchResponse.status}).`);
      }
    } catch (err: any) {
      setResetMfaFeedback({ type: 'error', text: err.message || 'No se pudo consultar la incidencia.' });
    } finally {
      setResetMfaSearching(false);
    }
  };

  const handleSendResetMfa = async () => {
    const incidencia = resetMfaIncidencia;
    if (!incidencia || resetMfaSending) return;
    if (!notificacionEsApiUrl || !notificacionEsApiKey) {
      setResetMfaFeedback({ type: 'error', text: 'Faltan variables de entorno para enviar el reseteo MF (ES).' });
      return;
    }

    // Líneas "Etiqueta: valor" de la descripción que genera alta_incidencia_jira
    const descripcionIncidencia = incidencia.descripcion || '';
    const campo = (etiqueta: string) =>
      descripcionIncidencia.match(new RegExp(`${etiqueta}:[ \\t]*([^\\n]*)`, 'i'))?.[1]?.trim() || '';
    const jsonIncidencia = {
      id_incidencia: incidencia.id_incidencia,
      perfil: campo('Perfil'),
      nombre: campo('Nombre') || incidencia.nombre || '',
      sede: campo('Sede'),
      telefono: campo('Teléfono'),
      mail_ceu: campo('Email CEU') || incidencia.mail_ceu || '',
      mail_personal: campo('Email personal'),
      descripcion: campo('Descripción') || descripcionIncidencia || incidencia.resumen || '',
    };
    if (!jsonIncidencia.mail_ceu) {
      setResetMfaFeedback({ type: 'error', text: 'La incidencia no tiene un correo CEU asociado.' });
      return;
    }

    setResetMfaSending(true);
    setResetMfaFeedback(null);
    try {
      const response = await fetch(`${notificacionEsApiUrl}?code=${notificacionEsApiKey}&accion=mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jsonIncidencia),
      });
      // La acción mfa responde con texto plano
      const text = (await response.text().catch(() => '')).trim();
      if (!response.ok || !/correctamente/i.test(text)) {
        throw new Error(text || `No se pudo enviar el reseteo MF (${response.status}).`);
      }
      setResetMfaFeedback({
        type: 'success',
        text: `Reseteo MF enviado a ${jsonIncidencia.nombre || jsonIncidencia.mail_ceu} (${jsonIncidencia.mail_ceu}).`,
      });
    } catch (err: any) {
      setResetMfaFeedback({ type: 'error', text: err.message || 'No se pudo enviar el reseteo MF.' });
    } finally {
      setResetMfaSending(false);
    }
  };

  const applyDateRange = () => {
    setDateFilterError(null);
    if (!startDateInput || !endDateInput) {
      setDateFilterError('Selecciona fecha de inicio y fin.');
      return;
    }
    const start = new Date(`${startDateInput}T00:00:00`);
    const end = new Date(`${endDateInput}T23:59:59.999`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setDateFilterError('Formato de fecha inválido.');
      return;
    }
    if (start.getTime() > end.getTime()) {
      setDateFilterError('La fecha de inicio no puede ser mayor que la fecha fin.');
      return;
    }
    setAppliedDateRange({ startMs: start.getTime(), endMs: end.getTime() });
  };

  const handleSave = async () => {
    const selectedVarsList = vars.filter((v) => v.selected);
    if (!selectedVarsList.length) {
      setVarError('Selecciona al menos una variable.');
      return;
    }

    setVarError(null);
    setSaving(true);
    try {
      // 1. Obtener el JSON actual del blob
      const configUrl = await getBlobServiceConfigUrl(serviceIdentifier);
      const configRes = await fetch(configUrl);
      let configData: any = {};
      if (configRes.ok) {
        configData = await configRes.json();
      }

      // 2. Modificar el diccionario "Variables"
      const varsAGuardar: Record<string, string> = {};
      for (const item of selectedVarsList) {
        varsAGuardar[item.prompt] = item.code;
      }
      configData['Variables'] = varsAGuardar;
      configData['VariablesCustomDefinitions'] = vars
        .filter((item) => item.source === 'custom')
        .map((item) => ({
          Nombre_variable_en_prompt: item.prompt,
          Nombre_variable_en_codigo: item.code,
          Decripcion: item.descripcion,
        }));

      // 2b. Asegurar que las variables personalizadas queden persistidas en el CSV global.
      const customVars = vars
        .filter((item) => item.source === 'custom')
        .map((item) => ({
          Nombre_variable_en_prompt: item.prompt.trim(),
          Nombre_variable_en_codigo: item.code.trim(),
          Decripcion: item.descripcion.trim(),
        }))
        .filter(
          (item) =>
            Boolean(item.Nombre_variable_en_prompt) &&
            Boolean(item.Nombre_variable_en_codigo)
        );

      if (customVars.length > 0) {
        const existingDefinitions = await readVariablesCsvFromBlob();
        const definitionsByPrompt = new Map<string, PromptVarCsvDefinition>();

        existingDefinitions.forEach((item) => {
          definitionsByPrompt.set(normalizeComparableText(item.Nombre_variable_en_prompt), item);
        });

        customVars.forEach((item) => {
          const key = normalizeComparableText(item.Nombre_variable_en_prompt);
          definitionsByPrompt.set(key, item);
        });

        const updatedDefinitions = Array.from(definitionsByPrompt.values());
        await writeVariablesCsvToBlob(updatedDefinitions);
      }
      
      const updatedJson = JSON.stringify(configData, null, 4);

      // 3. Guardar en el Blob Storage principal
      const putPrimaryUrl = await getBlobServiceConfigWriteUrl(serviceIdentifier);
      const primaryRes = await fetch(putPrimaryUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: updatedJson,
      });

      if (!primaryRes.ok) {
        throw new Error('Error al guardar la configuración de variables en el blob principal.');
      }

      // 4. Guardar en el Blob Storage secundario (si está disponible)
      try {
        const putSecondaryUrl = await getSecondaryBlobServiceConfigWriteUrl(serviceIdentifier);
        await fetch(putSecondaryUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: updatedJson,
        });
      } catch (e) {
        console.warn('No se pudo guardar la configuración en el blob secundario:', e);
      }

      const listaNombresVars = selectedVarsList.map(v => v.prompt);
      const listado = listaNombresVars.join(', ');
      const usoVariables = listaNombresVars.map(v => `*${v}*`).join(' ');
      
      const instruccionesHtml = `
            <div style="font-family:Segoe UI, Tahoma, sans-serif; color:#000;">
              <p>Se ha configurado las variables <strong>(${listado})</strong> en el servicio <strong>${serviceIdentifier}</strong>.</p>
              <p>Para utilizarlas en tu prompt, inclúyelas así:</p>
              <p style="background:#F1F1F1; padding:0.5rem; border-radius:4px; display:inline-block;">
                <code>${usoVariables}</code>
              </p>
              <p>Es importante que el nombre de cada variable vaya entre asteriscos <code>*</code>.</p>
            </div>
      `;

      setMessageOverlay({ show: true, text: instruccionesHtml });
    } catch (err: any) {
      setVarError(err.message || 'Error al guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFiles.length) {
      setPdfError('Selecciona al menos un archivo PDF.');
      return;
    }
    
    setPdfError(null);
    setPdfSuccess(null);
    setUploadingPdf(true);
    
    try {
      const configUrl = await getBlobServiceConfigUrl(serviceIdentifier);
      const configResPromise = fetch(configUrl);
      const uploadBatches = chunkArray(pdfFiles, PDF_UPLOAD_BATCH_SIZE);
      const uploadedFiles: Array<{ nombre: string; rutaBlob: string; azureFileId: string }> = [];

      for (const batch of uploadBatches) {
        const batchResults = await Promise.all(
          batch.map(async (file) => {
            const uuid = crypto.randomUUID();
            const folder = uuid;
            const fileName = `${uuid}.pdf`;
            const blobWriteUrl = await getBlobPdfWriteUrl(folder, fileName);

            const [blobRes, azureFileId] = await Promise.all([
              fetch(blobWriteUrl, {
                method: 'PUT',
                headers: {
                  'x-ms-blob-type': 'BlockBlob',
                  'Content-Type': 'application/pdf',
                },
                body: file,
              }),
              uploadFileToAzureOpenAI(file),
            ]);

            if (!blobRes.ok) {
              throw new Error(`Error al subir "${file.name}" al Blob Storage.`);
            }

            return {
              nombre: file.name,
              rutaBlob: blobWriteUrl.split('?')[0],
              azureFileId,
            };
          })
        );

        uploadedFiles.push(...batchResults);
      }

      const configRes = await configResPromise;

      let configData: any = {};

      if (configRes.ok) {
        configData = await configRes.json();
      } else {
        throw new Error('No se pudo cargar la configuración del JSON.');
      }

      let vectorStoreId = configData.vector_storage_id;

      if (uploadedFiles.length > 0) {
        if (!vectorStoreId) {
          const [firstFile, ...restFiles] = uploadedFiles;
          vectorStoreId = await createVectorStore(firstFile.azureFileId, `${serviceName}_vector_store`);
          configData.vector_storage_id = vectorStoreId;
          for (const batch of chunkArray(restFiles, PDF_UPLOAD_BATCH_SIZE)) {
            await Promise.all(batch.map((file) => addFileToVectorStore(vectorStoreId, file.azureFileId)));
          }
        } else {
          for (const batch of chunkArray(uploadedFiles, PDF_UPLOAD_BATCH_SIZE)) {
            await Promise.all(batch.map((file) => addFileToVectorStore(vectorStoreId, file.azureFileId)));
          }
        }
      }

      if (!configData.Documentos) {
        configData.Documentos = [];
      }
      configData.Documentos.push(...uploadedFiles);

      const updatedJson = JSON.stringify(configData, null, 4);

      // Save JSON to primary
      const putPrimaryUrl = await getBlobServiceConfigWriteUrl(serviceIdentifier);
      const primaryRes = await fetch(putPrimaryUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: updatedJson,
      });

      if (!primaryRes.ok) {
        throw new Error('Error al guardar el JSON del servicio en la cuenta principal.');
      }

      // Save JSON to secondary
      try {
        const putSecondaryUrl = await getSecondaryBlobServiceConfigWriteUrl(serviceIdentifier);
        await fetch(putSecondaryUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: updatedJson,
        });
      } catch (e) {
        console.warn('Advertencia: No se pudo actualizar el JSON en la cuenta secundaria.', e);
      }

      setPdfSuccess(
        uploadedFiles.length === 1
          ? 'El archivo PDF se ha subido exitosamente al asistente.'
          : `Se han subido ${uploadedFiles.length} archivos PDF exitosamente al asistente.`
      );
      setServiceDocuments(Array.isArray(configData.Documentos) ? [...configData.Documentos] : []);
      setPdfFiles([]);

      // Clear input element
      const fileInput = document.getElementById('pdf-upload-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (error: any) {
      setPdfError(error.message || 'Error inesperado al subir los PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleDeleteDocument = async (docToDelete: ServiceDocument) => {
    const confirmed = window.confirm(
      `Se eliminara "${docToDelete.nombre}" del asistente. ¿Quieres continuar?`
    );

    if (!confirmed) return;

    setPdfError(null);
    setPdfSuccess(null);
    setDeletingDocumentKey(docToDelete.rutaBlob);

    try {
      const configUrl = await getBlobServiceConfigUrl(serviceIdentifier);
      const configRes = await fetch(configUrl);

      if (!configRes.ok) {
        throw new Error('No se pudo cargar la configuración del JSON.');
      }

      const configData: any = await configRes.json();
      const documents: ServiceDocument[] = Array.isArray(configData.Documentos) ? [...configData.Documentos] : [];
      const documentIndex = documents.findIndex(
        (doc) => doc.rutaBlob === docToDelete.rutaBlob && doc.nombre === docToDelete.nombre
      );

      if (documentIndex === -1) {
        throw new Error('El documento ya no existe en la configuración actual del servicio.');
      }

      const vectorStoreId = configData.vector_storage_id;

      if (vectorStoreId && !docToDelete.azureFileId) {
        throw new Error('No se puede eliminar este documento del asistente porque no tiene azureFileId registrado.');
      }

      if (vectorStoreId && docToDelete.azureFileId) {
        await removeFileFromVectorStore(vectorStoreId, docToDelete.azureFileId);
      }

      if (docToDelete.azureFileId) {
        await deleteAzureOpenAIFile(docToDelete.azureFileId);
      }

      const blobDeleteUrl = await getBlobPdfDeleteUrl(docToDelete.rutaBlob);
      const blobDeleteRes = await fetch(blobDeleteUrl, {
        method: 'DELETE',
      });

      if (!blobDeleteRes.ok && blobDeleteRes.status !== 404) {
        throw new Error('No se pudo eliminar el archivo PDF del Blob Storage.');
      }

      documents.splice(documentIndex, 1);
      configData.Documentos = documents;

      const updatedJson = JSON.stringify(configData, null, 4);

      const putPrimaryUrl = await getBlobServiceConfigWriteUrl(serviceIdentifier);
      const primaryRes = await fetch(putPrimaryUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: updatedJson,
      });

      if (!primaryRes.ok) {
        throw new Error('Error al guardar el JSON del servicio en la cuenta principal.');
      }

      try {
        const putSecondaryUrl = await getSecondaryBlobServiceConfigWriteUrl(serviceIdentifier);
        await fetch(putSecondaryUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: updatedJson,
        });
      } catch (e) {
        console.warn('Advertencia: No se pudo actualizar el JSON en la cuenta secundaria.', e);
      }

      setServiceDocuments(documents);
      setPdfSuccess(`"${docToDelete.nombre}" se ha eliminado del asistente.`);
    } catch (error: any) {
      setPdfError(error.message || 'Error inesperado al eliminar el PDF.');
    } finally {
      setDeletingDocumentKey(null);
    }
  };

  const handlePublishPrompt = async () => {
    if (domainInstructions) return;
    const fileName = promptFileName.trim();
    if (!fileName) {
      setPromptPublishError('No se ha podido identificar el archivo de prompt desde la configuración del servicio.');
      return;
    }

    const primaryBlobConnectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
    const secondaryBlobConnectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
    const promptsContainerName = (process.env.REACT_APP_SM_PROMPTS_CONTAINER_NAME as string) || 'promptsprocesos';
    const promptsFolderName = (process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string) || 'desarrollo';
    const promptsDeployedFolderName = (process.env.REACT_APP_SM_PROMPTS_DEPLOYED_FOLDER_NAME as string) || 'desplegado';

    if (!primaryBlobConnectionString || !secondaryBlobConnectionString) {
      setPromptPublishError('Faltan las connection strings de blob principal o secundario para publicar el prompt.');
      return;
    }

    setPublishingPrompt(true);
    setPromptPublishError(null);
    setPromptPublishSuccess(null);

    const publishForConnectionString = async (connectionString: string) => {
      const sourcePromptBlob = buildPromptBlobPath(promptsFolderName, fileName);
      const deployedPromptBlob = buildDeployedPromptBlobPath(promptsDeployedFolderName, fileName);

      const sourcePromptReadUrl = await getBlobUrlByConnectionString(
        connectionString,
        promptsContainerName,
        sourcePromptBlob,
        'r'
      );
      const sourceRes = await fetch(sourcePromptReadUrl);
      if (!sourceRes.ok) {
        throw new Error(`No se pudo leer ${sourcePromptBlob} (${sourceRes.status}).`);
      }

      const promptContent = await sourceRes.text();
      const deployedPromptWriteUrl = await getBlobUrlByConnectionString(
        connectionString,
        promptsContainerName,
        deployedPromptBlob,
        'rw'
      );
      const deployedRes = await fetch(deployedPromptWriteUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'text/plain; charset=utf-8',
        },
        body: promptContent,
      });

      if (!deployedRes.ok) {
        throw new Error(`No se pudo publicar ${deployedPromptBlob} (${deployedRes.status}).`);
      }

      return getResponseLastModifiedMs(deployedRes) ?? Date.now();
    };

    try {
      const deployedModifiedDates = await Promise.all([
        publishForConnectionString(primaryBlobConnectionString),
        publishForConnectionString(secondaryBlobConnectionString),
      ]);
      setDeployedPromptLastModifiedMs(Math.max(...deployedModifiedDates));
      setDeployedPromptError(null);
      setPromptPublishSuccess(`Prompt "${fileName}" puesto en producción correctamente.`);
    } catch (error: any) {
      setPromptPublishError(error.message || 'No se pudo publicar el prompt en producción.');
    } finally {
      setPublishingPrompt(false);
    }
  };

  const handleUtdCellChange = (rowIndex: number, header: string, value: string) => {
    setUtdProcessRows((prev) =>
      prev.map((row, index) => (index === rowIndex ? { ...row, [header]: value } : row))
    );
    if (utdProcessError) setUtdProcessError(null);
    if (utdProcessSuccess) setUtdProcessSuccess(null);
  };

  const handleBecasUserFieldChange = (
    rowIndex: number,
    header: string,
    userIndex: number,
    value: string
  ) => {
    setUtdProcessRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;
        const values = splitBecasUserValues(row[header] || '');
        while (values.length <= userIndex) values.push('');
        values[userIndex] = value;
        return { ...row, [header]: serializeBecasUserValues(values) };
      })
    );
    if (utdProcessError) setUtdProcessError(null);
    if (utdProcessSuccess) setUtdProcessSuccess(null);
  };

  const toggleBecasUserCard = (cardKey: string) => {
    setCollapsedBecasUsers((prev) => {
      const isCollapsed = prev[cardKey] ?? true;
      return { ...prev, [cardKey]: !isCollapsed };
    });
  };

  const handleAddUtdRow = () => {
    const headers = utdProcessHeaders.length
      ? utdProcessHeaders
      : getDefaultServiceTableHeaders(serviceTableSource || 'utd');
    if (!utdProcessHeaders.length) {
      setUtdProcessHeaders(headers);
    }
    const newIndex = utdProcessRows.length;
    setUtdProcessRows((prev) => [...prev, createEmptyUtdProcessRow(headers)]);
    setUtdEditorRowIndex(newIndex);
    setUtdEditorIsNew(true);
    setCollapsedBecasUsers({});
    setUtdVisibleCount((prev) => Math.max(prev, newIndex + 1));
    if (utdProcessError) setUtdProcessError(null);
    if (utdProcessSuccess) setUtdProcessSuccess(null);
  };

  const handleOpenUtdEditor = (rowIndex: number) => {
    setUtdEditorRowIndex(rowIndex);
    setUtdEditorIsNew(false);
    setCollapsedBecasUsers({});
  };

  const handleCloseUtdEditor = () => {
    if (utdEditorIsNew && utdEditorRowIndex !== null) {
      const emptyRow = utdProcessRows[utdEditorRowIndex];
      const isEmpty =
        emptyRow &&
        Object.values(emptyRow).every((value) => !String(value || '').trim());
      if (isEmpty) {
        setUtdProcessRows((prev) => prev.filter((_, i) => i !== utdEditorRowIndex));
      }
    }
    setUtdEditorRowIndex(null);
    setUtdEditorIsNew(false);
  };

  const handleRemoveUtdRow = (rowIndex: number) => {
    setUtdProcessRows((prev) => prev.filter((_, index) => index !== rowIndex));
    if (utdProcessError) setUtdProcessError(null);
    if (utdProcessSuccess) setUtdProcessSuccess(null);
  };

  const handleSaveUtdProcesses = async () => {
    if (!serviceTableSource) return;
    try {
      setSavingUtdProcesses(true);
      setUtdProcessError(null);
      setUtdProcessSuccess(null);

      const primaryConnectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
      const secondaryConnectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
      const connectionTargets = serviceTableSource === 'becasColab'
        ? [
            { label: 'blob primario', connectionString: primaryConnectionString },
            { label: 'blob secundario', connectionString: secondaryConnectionString },
          ]
        : [
            { label: 'blob secundario', connectionString: secondaryConnectionString },
          ];
      const containerName = serviceTableContainerName;

      if (!containerName || connectionTargets.some((target) => !target.connectionString)) {
        throw new Error(`Faltan variables de entorno para guardar ${serviceTableFileName}.`);
      }

      const headers = utdProcessHeaders.length
        ? utdProcessHeaders
        : getDefaultServiceTableHeaders(serviceTableSource);
      const fileContent = serviceTableSource === 'becasColab'
        ? buildBecasJson(headers, utdProcessRows)
        : buildUtdProcessCsv(headers, utdProcessRows);
      const contentType = serviceTableSource === 'becasColab'
        ? 'application/json; charset=utf-8'
        : 'text/csv; charset=utf-8';

      await Promise.all(connectionTargets.map(async ({ label, connectionString }) => {
        const writeUrl = await getBlobUrlByConnectionString(
          connectionString,
          containerName,
          serviceTableFileName,
          'rw'
        );
        const response = await fetch(writeUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': contentType,
          },
          body: fileContent,
        });

        if (!response.ok) {
          throw new Error(`No se pudo guardar ${serviceTableFileName} en ${label} (${response.status}).`);
        }
      }));

      if (serviceTableSource === 'becasColab') {
        await sendBecasCampusPdfsByEmail(utdProcessRows, user);
      }

      setUtdProcessHeaders(headers);
      setUtdProcessSnapshot(serializeUtdProcessSnapshot(headers, utdProcessRows));
      setUtdProcessSuccess(
        serviceTableSource === 'becasColab'
          ? `Se ha guardado correctamente`
          : 'Se ha guardado correctamente.'
      );
    } catch (err: any) {
      setUtdProcessError(err.message || `No se pudieron guardar los cambios de ${serviceTableTitle}.`);
    } finally {
      setSavingUtdProcesses(false);
    }
  };


  if (!serviceName) {
    return (
      <div className="error-container">
        <h2>Servicio no especificado</h2>
      </div>
    );
  }

  if (loadingAccess) {
    return <SMLoadingSpinner />;
  }

  if (accessError) {
    return (
      <div className="error-container">
        <AlertCircle size={48} className="error-icon" />
        <h2>No se ha podido validar el acceso al servicio</h2>
        <p>{accessError}</p>
        <Link to="/gestion-servicios" className="btn-outline-back">
          <ChevronLeft size={18} />
          Volver al panel principal
        </Link>
      </div>
    );
  }

  if (!hasServiceAccess) {
    return (
      <div className="error-container">
        <AlertCircle size={48} className="error-icon" />
        <h2>Acceso denegado</h2>
        <p>No tienes permisos para acceder a este servicio.</p>
        <Link to="/gestion-servicios" className="btn-outline-back">
          <ChevronLeft size={18} />
          Volver al panel principal
        </Link>
      </div>
    );
  }

  return (
    <div className="service-config-page">
      <div className="service-config-header-actions">
        <Link to="/gestion-servicios" className="btn-outline-back">
          <ChevronLeft size={18} />
          Volver al panel principal
        </Link>
        <a href="/documentacion_prompts/index.html" target="_blank" rel="noopener noreferrer" className="btn-doc">
          Abrir documentación
          <ExternalLink size={14} />
        </a>
      </div>

      <h1 className="service-initiative-title">{serviceName}</h1>
      {serviceId && <p className="service-id-subtitle">ID servicio: {serviceId}</p>}

      <div className="service-layout-v2">
        <div className="service-top-grid">
          <section className="service-config-block prompt-file-block prompt-file-block-top">
            <div className="prompt-top-head">
              <div>
                <h2>Prompt del servicio</h2>
                <p className="service-block-subtitle">
                  Accede al archivo principal del asistente y editalo en una vista ampliada.
                </p>
              </div>
             
            </div>

            {loadingText ? (
              <SMLoadingSpinner />
            ) : (
              <>
                {textError && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {textError}
                  </div>
                )}

                <a
                  href={`/gestion-servicios/servicio/${encodeURIComponent(serviceName)}/editor${(effectiveServiceId || serviceId) ? `?sid=${encodeURIComponent(effectiveServiceId || serviceId)}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-preview-card prompt-preview-card"
                >
                  <div className="file-preview-left">
                    <div className="file-preview-icon">
                      <FileText size={20} />
                    </div>
                    <div>
                      <p className="file-preview-name">
                        {(instructionFiles.length > 1 || domainInstructions) ? instructionFiles[0].nombre : promptFileName || `${serviceName}.txt`}
                      </p>
                      <p className="file-preview-meta">{editablePromptText.length} caracteres</p>
                    </div>
                  </div>
                  <span className="file-preview-action">
                    <Maximize2 size={14} />
                    Abrir editor grande
                  </span>
                </a>

                {instructionFiles.length > 1 && (
                  <div className="prompt-file-list">
                    {instructionFiles.slice(1).map((file) => (
                      <a
                        key={file.ruta}
                        href={`/gestion-servicios/servicio/${encodeURIComponent(serviceName)}/editor?${new URLSearchParams({
                          ...((effectiveServiceId || serviceId) ? { sid: effectiveServiceId || serviceId } : {}),
                          archivo: file.nombre,
                        }).toString()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-preview-card"
                      >
                        <div className="file-preview-left">
                          <div className="file-preview-icon">
                            <FileText size={16} />
                          </div>
                          <div>
                            <p className="file-preview-name">{file.nombre}</p>
                            <p className="file-preview-meta">
                              {file.nombre.startsWith('knowledge/')
                                ? 'Archivo de conocimiento (knowledge)'
                                : 'Instrucciones adicionales'}
                            </p>
                          </div>
                        </div>
                        <span className="file-preview-action">
                          <Maximize2 size={14} />
                          Abrir
                        </span>
                      </a>
                    ))}
                  </div>
                )}

                {domainInstructions && (
                  <p className="file-preview-meta">Las instrucciones se guardan en el dominio y el agente las lee automáticamente.</p>
                )}
                {canPublishPrompt && (
                  <div
                    className={`prompt-publish-panel${isPromptPendingPublish ? ' prompt-publish-panel-highlight' : ''}${isPromptOutdated ? ' prompt-publish-panel-outdated' : ''}${isPromptPublished ? ' prompt-publish-panel-deployed' : ''}`}
                  >
                    <div>
                      <strong>Publicación del prompt</strong>
                      <p>
                        {instructionFiles.length > 1
                          ? `Publica en producción la versión actual de desarrollo del archivo principal (${instructionFiles[0].nombre}).`
                          : 'Publica la versión actual de desarrollo en producción.'}
                      </p>
                      <span className="prompt-deployed-status">{deployedPromptStatus}</span>
                      <div className="prompt-date-comparison">
                        <span>Desarrollo: {formatBlobModifiedDate(promptLastModifiedMs)}</span>
                        <span>Producción: {formatBlobModifiedDate(deployedPromptLastModifiedMs)}</span>
                      </div>
                    </div>
                    {deployedPromptError && <div className="error-alert">{deployedPromptError}</div>}
                    {promptPublishError && <div className="error-alert">{promptPublishError}</div>}
                    {promptPublishSuccess && <div className="success-alert">{promptPublishSuccess}</div>}
                    <button
                      type="button"
                      className="btn-configure prompt-publish-btn"
                      onClick={handlePublishPrompt}
                      disabled={publishingPrompt || !promptFileName}
                    >
                      {publishingPrompt ? (
                        <RefreshCw className="spinner-icon" size={16} />
                      ) : (
                        <UploadCloud size={16} />
                      )}
                      {publishingPrompt ? 'Publicando...' : 'Publicar'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="service-config-block knowledge-block">
            <div className="service-card-head">
              <div className="knowledge-title-row">
                <span className="knowledge-title-icon" aria-hidden="true">
                  <Files size={18} />
                </span>
                <h2>Documentos</h2>
              </div>
              <span className="service-side-chip">{serviceDocuments.length}</span>
            </div>

            <div className="knowledge-manager">
              <div className="knowledge-manager-toolbar">
                <label htmlFor="pdf-upload-input" className="pdf-upload-inline">
                  <input
                    id="pdf-upload-input"
                    className="pdf-upload-input"
                    type="file"
                    multiple
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const selectedFiles = Array.from(e.target.files);
                        setPdfFiles((prevFiles) => mergeSelectedFiles(prevFiles, selectedFiles));
                        setPdfError(null);
                        setPdfSuccess(null);
                        e.target.value = '';
                      }
                    }}
                  />
                  <span className="pdf-upload-icon">
                    <Plus size={16} />
                  </span>
                  <span className="pdf-upload-inline-copy">
                    <span className="pdf-upload-inline-title">Nuevos PDF</span>
                    <span
                      className="pdf-upload-inline-name"
                      title={pdfFiles.length ? pdfFiles.map((file) => file.name).join(', ') : 'Seleccionar archivos PDF'}
                    >
                      {pdfFiles.length === 0
                        ? 'Seleccionar archivos'
                        : pdfFiles.length === 1
                          ? pdfFiles[0].name
                          : `${pdfFiles.length} archivos seleccionados`}
                    </span>
                  </span>
                </label>

                <button
                  className="btn-configure pdf-upload-btn pdf-upload-btn-compact"
                  onClick={handleUploadPdf}
                  disabled={!pdfFiles.length || uploadingPdf}
                  aria-label={uploadingPdf ? 'Subiendo PDF' : 'Subir PDF'}
                >
                  {uploadingPdf ? <RefreshCw className="spinner-icon" size={16} /> : <UploadCloud size={16} />}
                  <span>{uploadingPdf ? 'Subiendo' : 'Subir'}</span>
                </button>
              </div>

              {serviceDocuments.length > 0 ? (
                <div className="uploaded-documents-list">
                  {visibleServiceDocuments.map((doc, index) => (
                    <div key={`${doc.rutaBlob}-${index}`} className="uploaded-document-card">
                      <div className="uploaded-document-main">
                        <div className="file-preview-icon uploaded-document-icon">
                          <FileText size={16} />
                        </div>
                        <p className="uploaded-document-name" title={doc.nombre || `Documento ${index + 1}`}>
                          {doc.nombre || `Documento ${index + 1}`}
                        </p>
                      </div>
                      <div className="uploaded-document-actions">
                        <a
                          href={doc.rutaBlob}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="uploaded-document-icon-btn"
                          aria-label={`Ver ${doc.nombre || `Documento ${index + 1}`}`}
                          title="Ver PDF"
                        >
                          <ExternalLink size={15} />
                        </a>
                        <button
                          type="button"
                          className="uploaded-document-icon-btn uploaded-document-icon-btn-danger"
                          onClick={() => handleDeleteDocument(doc)}
                          disabled={deletingDocumentKey === doc.rutaBlob}
                          aria-label={`Eliminar ${doc.nombre || `Documento ${index + 1}`}`}
                          title="Eliminar PDF"
                        >
                          {deletingDocumentKey === doc.rutaBlob ? (
                            <RefreshCw className="spinner-icon" size={15} />
                          ) : (
                            <Trash2 size={15} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                  {serviceDocuments.length > VISIBLE_DOCUMENTS_LIMIT && (
                    <button
                      type="button"
                      className="uploaded-documents-toggle"
                      onClick={() => setShowAllDocuments((prev) => !prev)}
                    >
                      {showAllDocuments
                        ? 'Ver menos'
                        : `Ver ${serviceDocuments.length - VISIBLE_DOCUMENTS_LIMIT} documentos mas`}
                    </button>
                  )}
                </div>
              ) : (
                <div className="knowledge-empty-state">
                  <FileText size={16} />
                  <span>Sin documentos</span>
                </div>
              )}
            </div>

            {pdfError && (
              <div className="error-alert pdf-feedback">
                <AlertCircle size={16} />
                {pdfError}
              </div>
            )}

            {pdfSuccess && <div className="success-banner pdf-feedback">{pdfSuccess}</div>}
          </section>
        </div>

        {serviceTableSource && (
          <section className="service-config-block utd-block">
            <div className="service-card-head">
              <div>
                <h2>{serviceTableTitle}</h2>
                <p className="service-block-subtitle">
                  Gestiona la lista de {serviceTableItemName}s. Haz clic en cualquier registro para editarlo.
                </p>
              </div>
              <span className="service-side-chip">{utdProcessRows.length}</span>
            </div>

            {loadingUtdProcesses ? (
              <SMLoadingSpinner />
            ) : (
              <>
                <div className="utd-toolbar">
                  <div className="utd-toolbar-search">
                    <input
                      type="search"
                      className="utd-search-input"
                      placeholder={
                        serviceTableSource === 'becasColab'
                          ? 'Buscar por ID, titulo, campus...'
                          : 'Buscar por ID, nombre, centro...'
                      }
                      value={utdSearch}
                      onChange={(e) => {
                        setUtdSearch(e.target.value);
                        setUtdVisibleCount(10);
                      }}
                    />
                    <span className="utd-toolbar-count">
                      {filteredUtdRowIndexes.length} de {utdProcessRows.length}
                    </span>
                  </div>
                  <div className="utd-toolbar-actions">
                  
                    <button
                      type="button"
                      className="utd-secondary-btn"
                      onClick={handleAddUtdRow}
                      aria-label="Nuevo registro"
                      title="Nuevo registro"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn-configure utd-save-btn"
                      onClick={handleSaveUtdProcesses}
                      disabled={savingUtdProcesses || !hasUtdProcessChanges}
                      aria-label={savingUtdProcesses ? 'Guardando cambios' : 'Guardar cambios'}
                      title={savingUtdProcesses ? 'Guardando cambios' : 'Guardar cambios'}
                    >
                      {savingUtdProcesses ? <RefreshCw className="spinner-icon" size={16} /> : <Save size={16} />}
                    </button>
                  </div>
                </div>

                {utdProcessError && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {utdProcessError}
                  </div>
                )}

                {utdProcessSuccess && <div className="success-banner">{utdProcessSuccess}</div>}

                {utdProcessRows.length === 0 ? (
                  <div className="utd-empty-card">
                    <p className="utd-empty-card-title">Todavia no hay registros</p>
                    <p className="utd-empty-card-text">
                      Crea el primer registro para empezar a rellenar la lista de {serviceTableTitle}.
                    </p>
                    <button
                      type="button"
                      className="utd-secondary-btn"
                      onClick={handleAddUtdRow}
                      aria-label="Anadir primer registro"
                      title="Anadir primer registro"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                ) : filteredUtdRowIndexes.length === 0 ? (
                  <p className="utd-preview-note">No hay registros que coincidan con la busqueda.</p>
                ) : (
                  <ul className="utd-process-list">
                    {filteredUtdRowIndexes.slice(0, utdVisibleCount).map((rowIndex) => {
                      const row = utdProcessRows[rowIndex];
                      const title =
                        (utdPrimaryHeader && row[utdPrimaryHeader]) ||
                        `${serviceTableItemName === 'beca' ? 'Beca' : 'Proceso'} ${rowIndex + 1}`;
                      const id = utdIdHeader
                        ? formatServiceTableId(serviceTableSource, utdIdHeader, row[utdIdHeader])
                        : '';
                      const secondary =
                        utdSecondaryHeader && row[utdSecondaryHeader]
                          ? row[utdSecondaryHeader]
                          : '';

                      return (
                        <li key={`utd-item-${rowIndex}`} className="utd-process-item">
                          <button
                            type="button"
                            className="utd-process-main"
                            onClick={() => handleOpenUtdEditor(rowIndex)}
                            aria-label={`Editar ${title || serviceTableItemName}`}
                            title={`Editar ${title || serviceTableItemName}`}
                          >
                            <div className="utd-process-body">
                              <div className="utd-process-title-row">
                                {id && <span className="utd-process-id">{id}</span>}
                                <span className="utd-process-title">{title || 'Sin nombre'}</span>
                              </div>
                              {secondary && (
                                <p className="utd-process-sub">{secondary}</p>
                              )}
                            </div>
                            <span className="utd-process-edit-hint">
                              <FilePenLine size={14} />
                            </span>
                          </button>
                          <button
                            type="button"
                            className="utd-process-delete"
                            onClick={() => handleRemoveUtdRow(rowIndex)}
                            aria-label={`Eliminar ${title || serviceTableItemName}`}
                            title="Eliminar"
                          >
                            <Trash2 size={15} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {filteredUtdRowIndexes.length > utdVisibleCount && (
                  <div className="utd-load-more">
                    <span className="utd-load-more-info">
                      Mostrando {utdVisibleCount} de {filteredUtdRowIndexes.length}
                    </span>
                    <div className="utd-load-more-actions">
                      <button
                        type="button"
                        className="utd-secondary-btn"
                        aria-label="Ver 10 mas"
                        title="Ver 10 mas"
                        onClick={() =>
                          setUtdVisibleCount((prev) =>
                            Math.min(prev + 10, filteredUtdRowIndexes.length)
                          )
                        }
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        type="button"
                        className="utd-secondary-btn"
                        onClick={() => setUtdVisibleCount(filteredUtdRowIndexes.length)}
                        aria-label="Ver todos"
                        title="Ver todos"
                      >
                        <Maximize2 size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        <div className="service-config-block service-vars-full" id="var-config-container">
          <h2>Listado de variables para el prompt</h2>

          <section className="variables-config">
            <h3>Gestión de variables del prompt</h3>
            <p>En esta sección tienes todo lo necesario para gestionar las variables que se usan en el prompt:</p>
            <ol>
              <li>
                <strong>Listado de variables disponibles</strong>
                <p>Aquí verás todas las variables que puedes incorporar en tus prompts.</p>
              </li>
              <li>
                <strong>Variables activas o ya configuradas</strong>
                <p>Además, se muestran aquellas variables que ya estás utilizando o que habías configurado con anterioridad.</p>
              </li>
              <li>
                <strong>Cómo dejar de usar una variable</strong>
                <ul>
                  <li>Desmarca la casilla junto al nombre de la variable que quieras desactivar.</li>
                  <li>Haz clic en <strong>Guardar variable</strong> para que el cambio se aplique.</li>
                </ul>
              </li>
            </ol>
            <p>De esta manera puedes añadir, revisar o retirar variables de forma rápida y sencilla según tus necesidades.</p>
          </section>

          {loadingVars ? (
            <SMLoadingSpinner />
          ) : (
            <>
              {varError && !vars.length && (
                <div className="error-alert">
                  <AlertCircle size={16} />
                  {varError}
                </div>
              )}

              <div id="var-grid">
                {vars.map((v) => (
                  <div
                    key={v.prompt}
                    className={`var-card ${v.selected ? 'selected' : ''}`}
                    onClick={() => toggleVar(v.prompt)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleVar(v.prompt);
                      }
                    }}
                  >
                    <div className="var-name" title={v.prompt}>
                      {v.prompt}
                    </div>
                    <div className="var-desc">{v.descripcion}</div>
                    {isAdmin && (
                      <div className="var-card-actions">
                        <button
                          type="button"
                          className="var-card-action-btn"
                          aria-label={`Editar variable ${v.prompt}`}
                          title="Editar variable"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenEditVarForm(v);
                          }}
                          disabled={editingVarSaving || deletingVarPrompt === v.prompt}
                        >
                          <FilePenLine size={14} />
                        </button>
                        <button
                          type="button"
                          className="var-card-action-btn var-card-action-btn-danger"
                          aria-label={`Eliminar variable ${v.prompt}`}
                          title="Eliminar variable del CSV"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleDeleteVarFromCsv(v);
                          }}
                          disabled={editingVarSaving || deletingVarPrompt === v.prompt}
                        >
                          {deletingVarPrompt === v.prompt ? (
                            <RefreshCw className="spinner-icon" size={14} />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {isAdmin && (
                  <button
                    type="button"
                    className="var-add-card"
                    aria-label="Añadir variable personalizada"
                    title="Añadir variable personalizada"
                    onClick={handleToggleCreateVarForm}
                  >
                    <Plus size={20} />
                    <span>Añadir variable</span>
                  </button>
                )}
              </div>

              <div id="var-footer">
                {varError && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    {varError}
                  </div>
                )}
                <button id="btn-configure" onClick={handleSave} disabled={saving}>
                  {saving ? <RefreshCw className="spinner-icon" size={18} /> : <Save size={18} />}
                  {saving ? 'Guardando...' : 'Guardar variables'}
                </button>
              </div>
            </>
          )}
        </div>

        {isAccidentePracticasService && (
          <AtencionPracticasPanel
            user={user}
            apiUrl={conversationBackend === 'ES' ? notificacionEsApiUrl : notificacionSweApiUrl}
            apiKey={conversationBackend === 'ES' ? notificacionEsApiKey : notificacionSweApiKey}
          />
        )}
        {isImpagosUchService && (
          <WhatsappChatPanel
            user={user}
            prefijo="impagos_chat_"
            titulo="Conversaciones WhatsApp · Impagos"
            apiUrl={conversationBackend === 'ES' ? notificacionEsApiUrl : notificacionSweApiUrl}
            apiKey={conversationBackend === 'ES' ? notificacionEsApiKey : notificacionSweApiKey}
          />
        )}

        <section className="service-config-block service-conversations-block">
          <div className="conv-header">
            <div>
              <h2>Conversaciones - {serviceName}</h2>

            </div>
          </div>

          <div className="conv-stats-grid">
            <div className="conv-stat-card">
              <span className="conv-stat-label">Total global</span>
              <span className="conv-stat-value">{conversationStats.totalGlobal}</span>
            </div>
            <div className="conv-stat-card">
              <span className="conv-stat-label">Conversaciones visibles</span>
              <span className="conv-stat-value">{visibleConversationCount}</span>
            </div>
            <div className="conv-stat-card">
              <span className="conv-stat-label">Rango</span>
              <span className="conv-stat-value-sm">{rangeLabel}</span>
            </div>
          </div>

          <div className="conv-controls">
            <select
              className="conv-backend-select"
              value={conversationBackend}
              onChange={(e) => setConversationBackend(e.target.value as 'ES' | 'SWE')}
              aria-label="Seleccionar backend de conversaciones"
            >
              <option value="ES">ES</option>
              <option value="SWE">SWE</option>
            </select>
            <div className="conv-date-filter conv-date-filter-priority">
              <p className="conv-date-title">
                <CalendarDays size={18} />
                Selecciona el intervalo de fechas
              </p>
              <div className="conv-date-grid">
                <label className="conv-date-field">
                  <span>Desde</span>
                  <input
                    type="date"
                    className="conv-date-input"
                    value={startDateInput}
                    onChange={(e) => setStartDateInput(e.target.value)}
                    aria-label="Fecha inicio"
                  />
                </label>
                <label className="conv-date-field">
                  <span>Hasta</span>
                  <input
                    type="date"
                    className="conv-date-input"
                    value={endDateInput}
                    onChange={(e) => setEndDateInput(e.target.value)}
                    aria-label="Fecha fin"
                  />
                </label>
                <button type="button" className="conv-apply-btn" onClick={applyDateRange}>
                  Aplicar intervalo
                </button>
              </div>
            </div>
          </div>

          {dateFilterError && (
            <div className="error-alert">
              <AlertCircle size={16} />
              {dateFilterError}
            </div>
          )}

          {!appliedDateRange ? (
            <p className="conv-empty">Selecciona un intervalo de fechas para ver conversaciones.</p>
          ) : (
            <>
              <div className="conv-controls">
                <input
                  className="conv-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por usuario o telefono"
                />
                <select
                  className="conv-rating-filter"
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value as typeof ratingFilter)}
                  aria-label="Filtrar por valoración"
                >
                  <option value="todas">Todas las conversaciones</option>
                  <option value="valoradas">Con valoración</option>
                  <option value="buena">Con respuestas buenas 👍</option>
                  <option value="mala">Con respuestas malas 👎</option>
                </select>
                <select
                  className="conv-rating-filter"
                  value={channelFilter}
                  onChange={(e) => setChannelFilter(e.target.value as typeof channelFilter)}
                  aria-label="Filtrar por vía de comunicación"
                >
                  <option value="todas">Todas las vías</option>
                  <option value="web">{CHANNEL_LABELS.web}</option>
                  <option value="app">{CHANNEL_LABELS.app}</option>
                  <option value="whatsapp">{CHANNEL_LABELS.whatsapp}</option>
                  <option value="otro">{CHANNEL_LABELS.otro}</option>
                </select>
                {isGptCeuServiceSelected && (
                  <div className="conv-identity-filters" aria-label="Filtrar tipo de usuario">
                    <label className="conv-identity-filter">
                      <input
                        type="checkbox"
                        checked={hidePhoneConversations}
                        onChange={(e) => setHidePhoneConversations(e.target.checked)}
                      />
                      Ocultar teléfonos
                    </label>
                    <label className="conv-identity-filter">
                      <input
                        type="checkbox"
                        checked={hideEmailConversations}
                        onChange={(e) => setHideEmailConversations(e.target.checked)}
                      />
                      Ocultar correos
                    </label>
                  </div>
                )}
              </div>

              {loadingConversations ? (
                <SMLoadingSpinner />
              ) : conversationError ? (
                <div className="error-alert">
                  <AlertCircle size={16} />
                  {conversationError}
                </div>
              ) : filteredGroupedConversations.sortedKeys.length === 0 ? (
                <p className="conv-empty">No hay conversaciones para los filtros actuales.</p>
              ) : (
                filteredGroupedConversations.sortedKeys.map((dateKey) => (
                  <div key={dateKey} className="conv-group">
                    <p className="conv-group-date">
                      Fecha: {dateKey} ({filteredGroupedConversations.grouped[dateKey].length}{' '}
                      {filteredGroupedConversations.grouped[dateKey].length === 1 ? 'conversación' : 'conversaciones'})
                    </p>
                    {filteredGroupedConversations.grouped[dateKey].map((conv) => {
                      const isExpanded = Boolean(expandedConversations[conv.id]);
                      const impagosDraft = impagosMessageDrafts[conv.id] || '';
                      const isSendingImpagosMessage = Boolean(impagosSendingMessages[conv.id]);
                      const impagosFeedback = impagosMessageFeedback[conv.id];
                      const canSendSurvey = isAsistenciaItService && conv.canales.includes('whatsapp');
                      const isSendingSurvey = Boolean(surveySending[conv.userId]);
                      const convSurveyFeedback = surveyFeedback[conv.userId];
                      const surveySentInfo = canSendSurvey ? getSurveySentInfo(conv.userId) : null;
                      return (
                        <article key={conv.id} className="conv-card">
                          <button
                            className="conv-card-head"
                            onClick={() => toggleConversation(conv.id)}
                          >
                            <span className="conv-card-title">
                              {isEmailUserId(conv.userId) ? (
                                <span
                                  className="conv-user-email"
                                  title="Clic para copiar el correo"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleCopyConversationEmail(conv);
                                  }}
                                >
                                  {conv.userDisplay}
                                </span>
                              ) : (
                                conv.userDisplay
                              )}
                              {conv.canales.map((canal) => (
                                <span key={canal} className={`conv-channel conv-channel--${canal}`}>
                                  {CHANNEL_LABELS[canal]}
                                </span>
                              ))}
                              {copiedConversationId === conv.id && (
                                <span className="conv-copied-hint">Correo copiado</span>
                              )}
                            </span>
                            <span>{isExpanded ? '-' : '+'}</span>
                          </button>
                          {isExpanded && (
                            <div className="conv-card-body">
                              {conv.messages.map((msg, idx) => (
                                <div key={`${conv.id}-${idx}`} className={`conv-message ${msg.role === 'user' ? 'user' : 'service'}`}>
                                  <div className="conv-message-head">
                                    <div className="conv-message-role">
                                      <span className="conv-role">{msg.role || 'sin rol'}</span>
                                      {((msg.reason != null && msg.reason !== '' && msg.confidence != null && msg.confidence !== '') || msg.llamadaFuncion) && (
                                        <div className="conv-message-info-wrapper">
                                          <button
                                            type="button"
                                            className="conv-message-info-button"
                                            title={[
                                              msg.reason != null && msg.reason !== '' ? `Reason: ${String(msg.reason)}` : '',
                                              msg.confidence != null && msg.confidence !== '' ? `Confidence: ${String(msg.confidence)}` : '',
                                              msg.llamadaFuncion ? `Llamada a función: ${msg.llamadaFuncion}` : '',
                                            ].filter(Boolean).join(' · ')}
                                            aria-label="Mostrar reason, confidence y llamada a función"
                                            onClick={() => {
                                              const infoId = `${conv.id}-${idx}`;
                                              setOpenMessageInfo((current) => (current === infoId ? null : infoId));
                                            }}
                                          >
                                            i
                                          </button>
                                          {openMessageInfo === `${conv.id}-${idx}` && (
                                            <div className="conv-message-info-popover" role="status">
                                              {msg.reason != null && msg.reason !== '' && (
                                                <div><strong>Reason:</strong> {String(msg.reason)}</div>
                                              )}
                                              {msg.confidence != null && msg.confidence !== '' && (
                                                <div><strong>Confidence:</strong> {String(msg.confidence)}</div>
                                              )}
                                              {msg.llamadaFuncion && (
                                                <div><strong>Llamada a función:</strong> {msg.llamadaFuncion}</div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="conv-message-meta">
                                      {(msg.valoracion === 'buena' || msg.valoracion === 'mala') && (
                                        <span
                                          className={`conv-rating conv-rating--${msg.valoracion}`}
                                          title={`Valorada como ${msg.valoracion === 'buena' ? 'buena' : 'mala'} respuesta${msg.fechaValoracion ? ` el ${msg.fechaValoracion}` : ''}${msg.valoracionPorEncuesta24h ? ' (encuesta de WhatsApp de 24h)' : ''}`}
                                        >
                                          {msg.valoracion === 'buena' ? (
                                            <ThumbsUp size={13} aria-hidden="true" />
                                          ) : (
                                            <ThumbsDown size={13} aria-hidden="true" />
                                          )}
                                          {msg.valoracion === 'buena' ? 'Buena respuesta' : 'Mala respuesta'}
                                          {msg.valoracionPorEncuesta24h && ' · encuesta 24h'}
                                        </span>
                                      )}
                                      <span className="conv-ts">{msg.timestampLabel || '-'}</span>
                                    </div>
                                  </div>
                                  <div className="conv-message-content" dangerouslySetInnerHTML={{ __html: msg.contentHtml }} />
                                </div>
                              ))}
                              {canSendSurvey && (
                                <div className="conv-survey">
                                  <div className="conv-survey-text">
                                    Enviar por WhatsApp la encuesta de satisfacción (plantilla de 24h). La respuesta del usuario quedará registrada en esta conversación.
                                  </div>
                                  {surveySentInfo && (
                                    <div className="conv-survey-sent">
                                      Encuesta ya enviada {surveySentInfo.fecha ? `el ${surveySentInfo.fecha}` : 'en las últimas 24 horas'}
                                      {surveySentInfo.servicio ? ` (${surveySentInfo.servicio})` : ''}. Se podrá volver a enviar pasadas 24 horas.
                                    </div>
                                  )}
                                  {convSurveyFeedback && !(surveySentInfo && convSurveyFeedback.type === 'success') && (
                                    <div className={`conv-send-message-feedback ${convSurveyFeedback.type}`}>
                                      {convSurveyFeedback.text}
                                    </div>
                                  )}
                                  <div className="conv-send-message-actions">
                                    <button
                                      type="button"
                                      className="conv-send-message-btn"
                                      onClick={() => void handleSendWhatsappSurvey(conv)}
                                      disabled={isSendingSurvey || Boolean(surveySentInfo)}
                                    >
                                      {isSendingSurvey && <RefreshCw className="spinner-icon" size={15} />}
                                      {isSendingSurvey
                                        ? 'Enviando...'
                                        : surveySentInfo
                                          ? 'Encuesta enviada'
                                          : 'Enviar encuesta por WhatsApp'}
                                    </button>
                                  </div>
                                </div>
                              )}
                              {isImpagosUchService && (
                                <form
                                  className="conv-send-message-form"
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    void handleSendImpagosMessage(conv);
                                  }}
                                >
                                  <label className="conv-send-message-label" htmlFor={`impagos-message-${conv.id}`}>
                                    Enviar mensaje en esta conversación
                                  </label>
                                  <textarea
                                    id={`impagos-message-${conv.id}`}
                                    className="conv-send-message-textarea"
                                    value={impagosDraft}
                                    onChange={(e) => handleImpagosDraftChange(conv.id, e.target.value)}
                                    placeholder="Escribe el mensaje para el alumno..."
                                    rows={3}
                                    disabled={isSendingImpagosMessage}
                                  />
                                  {impagosFeedback && (
                                    <div className={`conv-send-message-feedback ${impagosFeedback.type}`}>
                                      {impagosFeedback.text}
                                    </div>
                                  )}
                                  <div className="conv-send-message-actions">
                                    <button
                                      type="submit"
                                      className="conv-send-message-btn"
                                      disabled={isSendingImpagosMessage || !impagosDraft.trim()}
                                    >
                                      {isSendingImpagosMessage && <RefreshCw className="spinner-icon" size={15} />}
                                      {isSendingImpagosMessage ? 'Enviando...' : 'Enviar mensaje'}
                                    </button>
                                  </div>
                                </form>
                              )}
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ))
              )}
            </>
          )}
        </section>

        {isItServiceSid && (
          <section className="service-config-block reset-mfa-block">
            <h2>Enviar reseteo MFA</h2>
            <form
              className="reset-mfa-form"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSearchResetMfaJira();
              }}
            >
              <div className="reset-mfa-search">
                <input
                  id="reset-mfa-jira-id"
                  type="text"
                  className="reset-mfa-input"
                  aria-label="ID de la incidencia de Jira"
                  value={resetMfaJiraId}
                  onChange={(e) => {
                    setResetMfaJiraId(e.target.value);
                    setResetMfaIncidencia(null);
                    setResetMfaFeedback(null);
                  }}
                  placeholder="ID de la incidencia de Jira (IT-12345)"
                  disabled={resetMfaSearching || resetMfaSending}
                />
                <button
                  type="submit"
                  className="reset-mfa-icon-btn"
                  title="Buscar incidencia"
                  aria-label="Buscar incidencia"
                  disabled={resetMfaSearching || resetMfaSending || !resetMfaJiraId.trim()}
                >
                  {resetMfaSearching ? <RefreshCw className="spinner-icon" size={18} /> : <Search size={18} />}
                </button>
              </div>
              {resetMfaIncidencia && (
                <div className="reset-mfa-card">
                  <div className="reset-mfa-card-head">
                    <strong>{resetMfaIncidencia.id_incidencia}</strong>
                    {resetMfaIncidencia.estado && <span className="reset-mfa-estado">{resetMfaIncidencia.estado}</span>}
                    <button
                      type="button"
                      className="reset-mfa-icon-btn reset-mfa-send"
                      title="Enviar reseteo MFA"
                      aria-label="Enviar reseteo MFA"
                      onClick={() => void handleSendResetMfa()}
                      disabled={resetMfaSearching || resetMfaSending}
                    >
                      {resetMfaSending ? <RefreshCw className="spinner-icon" size={18} /> : <Send size={18} />}
                    </button>
                  </div>
                  <p className="reset-mfa-desc">
                    {resetMfaIncidencia.descripcion || 'La incidencia no tiene descripción.'}
                  </p>
                </div>
              )}
              {resetMfaFeedback && (
                <div className={`conv-send-message-feedback ${resetMfaFeedback.type}`}>{resetMfaFeedback.text}</div>
              )}
            </form>
          </section>
        )}
      </div>

      {messageOverlay.show && (
        <div id="message-overlay" style={{ display: 'flex' }}>
          <div className="message-box">
            <div dangerouslySetInnerHTML={{ __html: messageOverlay.text }} />
            <button className="close-btn" onClick={() => setMessageOverlay({ show: false, text: '' })}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {isAdmin && showCreateVarForm && (
        <div className="editor-overlay var-create-overlay" onClick={handleToggleCreateVarForm}>
          <div className="editor-modal var-create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="editor-modal-header">
              <div>
                <h2>Nueva variable personalizada</h2>
                <p className="service-block-subtitle">
                  Define el nombre visible, descripción y nombre en código.
                </p>
              </div>
              <button
                type="button"
                className="editor-close-btn"
                onClick={handleToggleCreateVarForm}
                aria-label="Cerrar formulario de nueva variable"
              >
                ✕
              </button>
            </div>

            <div className="var-create-modal-body">
              <div className="var-create-grid">
                <label className="var-create-field">
                  <span>Nombre visible</span>
                  <input
                    type="text"
                    value={newVarPrompt}
                    onChange={(e) => setNewVarPrompt(e.target.value)}
                    placeholder="Ej: Nombre del alumno"
                  />
                </label>
                <label className="var-create-field">
                  <span>Descripción</span>
                  <input
                    type="text"
                    value={newVarDescription}
                    onChange={(e) => setNewVarDescription(e.target.value)}
                    placeholder="Ej: Nombre completo del estudiante"
                  />
                </label>
                <label className="var-create-field">
                  <span>Nombre en código</span>
                  <input
                    type="text"
                    value={newVarCode}
                    onChange={(e) => setNewVarCode(e.target.value)}
                    placeholder="Ej: student_name"
                  />
                </label>
              </div>

              {newVarError && (
                <div className="error-alert">
                  <AlertCircle size={16} />
                  {newVarError}
                </div>
              )}

              <div className="var-create-actions">
                <button type="button" className="utd-secondary-btn" onClick={handleToggleCreateVarForm}>
                  Cancelar
                </button>
                <button type="button" className="btn-configure" onClick={handleAddCustomVar}>
                  Añadir variable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && editingVarPrompt && (
        <div className="editor-overlay var-create-overlay" onClick={resetEditVarForm}>
          <div className="editor-modal var-create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="editor-modal-header">
              <div>
                <h2>Editar variable</h2>
                <p className="service-block-subtitle">
                  Modifica los datos de la variable y actualiza el CSV global.
                </p>
              </div>
              <button
                type="button"
                className="editor-close-btn"
                onClick={resetEditVarForm}
                aria-label="Cerrar edición de variable"
              >
                ✕
              </button>
            </div>

            <div className="var-create-modal-body">
              <div className="var-create-grid">
                <label className="var-create-field">
                  <span>Nombre visible</span>
                  <input
                    type="text"
                    value={editVarPrompt}
                    onChange={(e) => setEditVarPrompt(e.target.value)}
                    placeholder="Ej: Nombre del alumno"
                  />
                </label>
                <label className="var-create-field">
                  <span>Descripción</span>
                  <input
                    type="text"
                    value={editVarDescription}
                    onChange={(e) => setEditVarDescription(e.target.value)}
                    placeholder="Ej: Nombre completo del estudiante"
                  />
                </label>
                <label className="var-create-field">
                  <span>Nombre en código</span>
                  <input
                    type="text"
                    value={editVarCode}
                    onChange={(e) => setEditVarCode(e.target.value)}
                    placeholder="Ej: student_name"
                  />
                </label>
              </div>

              {editVarError && (
                <div className="error-alert">
                  <AlertCircle size={16} />
                  {editVarError}
                </div>
              )}

              <div className="var-create-actions">
                <button
                  type="button"
                  className="utd-secondary-btn"
                  onClick={resetEditVarForm}
                  disabled={editingVarSaving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-configure"
                  onClick={handleSaveVarEdition}
                  disabled={editingVarSaving}
                >
                  {editingVarSaving ? <RefreshCw className="spinner-icon" size={16} /> : <Save size={16} />}
                  {editingVarSaving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {utdEditorRowIndex !== null && utdEditingRow && (
        <div className="editor-overlay utd-editor-overlay" onClick={handleCloseUtdEditor}>
          <div className="editor-modal utd-editor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="editor-modal-header utd-editor-header">
              <div>
                <h2>
                  {utdEditorIsNew
                    ? `Nuevo ${serviceTableItemName}`
                    : `Editar ${serviceTableItemName}`}
                </h2>
                <p className="service-block-subtitle">
                  Completa los campos y pulsa "Guardar cambios" para subir el archivo al blob.
                </p>
              </div>

            </div>

            <div className="utd-editor-form">
              {utdProcessHeaders
                .filter((header) =>
                  !(serviceTableSource === 'becasColab' && (isBecasIdField(header) || isBecasUserField(header)))
                )
                .map((header) => {
                const value = utdEditingRow[header] || '';
                const multiline = isUtdMultilineField(header);
                const inputId = `utd-field-${normalizeComparableText(header).replace(/\s+/g, '-')}`;
                return (
                  <div
                    key={header}
                    className={`utd-editor-field${multiline ? ' utd-editor-field-wide' : ''}`}
                  >
                    <label className="utd-editor-label" htmlFor={inputId}>
                      {header}
                    </label>
                    {multiline ? (
                      <textarea
                        id={inputId}
                        className="utd-editor-textarea"
                        value={value}
                        onChange={(e) =>
                          handleUtdCellChange(utdEditorRowIndex, header, e.target.value)
                        }
                        rows={Math.max(4, Math.min(12, value.split('\n').length + 2))}
                      />
                    ) : (
                      <input
                        id={inputId}
                        type="text"
                        className="utd-editor-input"
                        value={value}
                        onChange={(e) =>
                          handleUtdCellChange(utdEditorRowIndex, header, e.target.value)
                        }
                      />
                    )}
                  </div>
                );
              })}
              {serviceTableSource === 'becasColab' && becasUserFieldHeaders.length > 0 && (
                <section className="becas-users-section">
                  <div className="becas-users-head">
                    <div>
                      <h3>Interesados</h3>
                      <p>Informacion agrupada por usuario segun los arrays del JSON.</p>
                    </div>
                    <span>{becasUserCount}</span>
                  </div>

                  {becasUserCount > 0 ? (
                    <div className="becas-user-card-grid">
                      {Array.from({ length: becasUserCount }, (_, userIndex) => {
                        const nameHeader = becasUserFieldHeaders.find(
                          ({ header }) => normalizeComparableText(header) === 'nombre_interesado'
                        )?.header;
                        const emailHeader = becasUserFieldHeaders.find(
                          ({ header }) => normalizeComparableText(header) === 'correo_interesado'
                        )?.header;
                        const userName =
                          (nameHeader ? getBecasUserValue(utdEditingRow, nameHeader, userIndex) : '') ||
                          `Usuario ${userIndex + 1}`;
                        const userEmail = emailHeader ? getBecasUserValue(utdEditingRow, emailHeader, userIndex) : '';
                        const pdfUrl = buildBecasInteresadoPdfUrl(userEmail);
                        const cardKey = `${utdEditorRowIndex}-${userIndex}`;
                        const isCollapsed = collapsedBecasUsers[cardKey] ?? true;
                        const fieldsId = `becas-user-fields-${cardKey}`;

                        return (
                          <article
                            key={`becas-user-${userIndex}`}
                            className={`becas-user-card${isCollapsed ? ' becas-user-card-collapsed' : ''}`}
                            onClick={() => toggleBecasUserCard(cardKey)}
                            title={isCollapsed ? 'Desplegar interesado' : 'Plegar interesado'}
                          >
                            <div className="becas-user-card-title">
                              <div className="becas-user-card-identity">
                                <span>{userIndex + 1}</span>
                                <strong>{userName}</strong>
                              </div>
                              <div className="becas-user-card-actions">
                                {pdfUrl && (
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="uploaded-document-icon-btn becas-user-pdf-btn"
                                    aria-label={`Ver PDF de ${userName}`}
                                    title="Ver PDF"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink size={15} />
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="becas-user-toggle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBecasUserCard(cardKey);
                                  }}
                                  aria-expanded={!isCollapsed}
                                  aria-controls={fieldsId}
                                  aria-label={isCollapsed ? 'Ver detalle del interesado' : 'Ocultar detalle del interesado'}
                                >
                                  <span className="becas-user-toggle-icon">{isCollapsed ? '+' : '-'}</span>
                                </button>
                              </div>
                            </div>
                            {!isCollapsed && (
                              <div
                                id={fieldsId}
                                className="becas-user-card-fields"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {becasUserFieldHeaders.map(({ header, label }) => {
                                  const inputId = `becas-user-${userIndex}-${normalizeComparableText(header).replace(/\s+/g, '-')}`;
                                  return (
                                    <label key={`${header}-${userIndex}`} className="becas-user-card-field" htmlFor={inputId}>
                                      <span>{label}</span>
                                      <input
                                        id={inputId}
                                        type="text"
                                        className="utd-editor-input"
                                        value={getBecasUserValue(utdEditingRow, header, userIndex)}
                                        onChange={(e) =>
                                          handleBecasUserFieldChange(
                                            utdEditorRowIndex,
                                            header,
                                            userIndex,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="becas-users-empty">No hay interesados informados para esta beca.</p>
                  )}
                </section>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;

